import DOMPurify from "dompurify";
import { useContext, createContext, useEffect, useState, useRef } from "react";
import api from "../services/api";
import { toast } from "react-toastify";
import { useAuth } from "./useAuth";
import moment from "moment";
import { publish } from "../utils/events";
import { compareVersions } from "compare-versions";
import { useChats } from "./useChats";
import { convertHTMLToText, getCopyAllText } from "../utils/utils";
import {
  sanitizeCodes,
  buildIcd10OptionSelectors,
  prioritizeByPrefix,
  isPasteAndPickListSupported,
  buildVerifySelectors,
  planPasteAndPickListRequests,
  dispatchPasteAndPickListPlan,
  newNotBilledMissEntries,
  codeListLabel,
  hasActionableMisses,
  formatNotBilledNotice,
  NOT_BILLED_MISS_REASON,
} from "../utils/billingCodesPaste";
import { notBilledTextApplies } from "../utils/notBilled";
import { losDatasetHasAnnualSlot } from "../utils/losCodeParse";
import { runPfPasteAndPickList } from "../utils/pfPickerDriver";
import { formatCodedListHtml } from "../utils/codedListHtml";
import { normalizePlanOfCareForSync } from "../utils/syncPlanOfCareShape";
import { applyIcd10SexGate } from "../utils/icd10SexGate";
import { buildFieldsByCode, deriveSlipRowSelector } from "../utils/chargeSlipMapping";
import { pickDefaultExecutionTab } from "../utils/ehrTabSelection";
import { resolveClickRetryPolicy, interpretProbe, runVerifiedClick } from "../utils/ehrClickRetry";
import { resolveProvider, parseAgeFromWidget } from "../utils/insuranceProviderMapping";
import { useTranslation } from "react-i18next";

export const ehrContext = createContext();

// How long a charge-slip step waits for any slip line to appear before concluding the
// slip never opened. Only spent when a step wrote nothing: an open slip answers on the
// first poll, a missing one is worth a few seconds of patience before going red.
const SLIP_PRESENCE_TIMEOUT_MS = 3000;

function useEhrProvider() {
  const { t } = useTranslation();
  const { auth, setIsChromeExtensionInstalled, brand } = useAuth();
  const [extensionID, setExtensionID] = useState(() => {
    if (auth?.user?.custom?.extensionId) {
      return auth.user.custom.extensionId;
    }

    if (brand === "rpg") {
      return process.env.REACT_APP_EXTENSION_ID_RPG || "";
    }

    if (brand === "healcore") {
      return process.env.REACT_APP_EXTENSION_ID_HEALCORE || "";
    }

    return process.env.REACT_APP_EXTENSION_ID || "";
  });
  const [version, setVersion] = useState("0.0.0");
  const [templates, setTemplates] = useState();

  // Utils
  const { updateContext } = useChats();
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [importingEncounters, setImportingEncounters] = useState(false);
  const selectedTabPromise = useRef(false);

  // Modal controls
  const [showModalSyncEhr, setShowModalSyncEhr] = useState(false);
  const [showModalSelectTab, setShowModalSelectTab] = useState(false);

  // Execution
  const maxRetries = 3;
  const currentSection = useRef("");
  const cancel = useRef(false);
  const template = useRef(false);
  const executionTabRef = useRef();
  const executionChatRef = useRef();
  const syncReportActions = useRef([]);
  const syncMissedCodes = useRef(null);
  // Tracks codes the ext auto-mapped (e.g. parent ICD M54.5 → unspecified child M54.50)
  // so we can surface a visible-substitution toast distinct from the missed-codes one.
  const syncSubstitutedCodes = useRef(null);
  const syncStartTime = useRef(null);
  const isTestSync = useRef(false);
  const failedSections = useRef([]);
  const [smartPasteQueue, setSmartPasteQueue] = useState(null);

  const [currentStep, setCurrentStep] = useState(0);
  const [totalStep, setTotalStep] = useState(0);
  const [executionTab, setExecutionTabFn] = useState();
  const [executionChat, setExecutionChatFn] = useState();

  // Execution results
  const [errors, setErrors] = useState([]);
  const [error, setError] = useState();
  const [didRun, setDidRun] = useState();
  const [executing, setExecuting] = useState(false);

  // ---------------
  // ---- Init -----
  // ---------------

  useEffect(() => {
    initExtension();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extensionID]);

  useEffect(() => {
    if (auth?.user?.custom?.extensionId) {
      setExtensionID(auth.user.custom.extensionId);
    }
  }, [auth?.user]);

  const initExtension = async () => {
    try {
      if (!window?.chrome?.runtime?.sendMessage) return false;

      let response = await sendMessageToExtension("getVersion");
      if (!window?.chrome.runtime.lastError) {
        console.log("Got extension version", response);
        if (response.error) {
          toast.error(t("hooks.useEHR.extensionNotConnected"));
        } else {
          // toast.success("Extension connected");
          setVersion(response);
          setIsChromeExtensionInstalled(true);
        }
      }
    } catch (e) {
      console.warn("Error finding extension:", extensionID, e);
    }
  };

  // ---------------
  // ---- Utils -----
  // ---------------

  const setExecutionTab = (tabId) => {
    setExecutionTabFn(tabId);
    executionTabRef.current = tabId;
  };

  const setExecutionChat = (chat) => {
    setExecutionChatFn(chat);
    executionChatRef.current = chat;
  };

  const requestTabs = async () => {
    try {
      let tabs = await sendMessageToExtension("getTabs");
      return tabs?.filter((t) => {
        return t.url?.indexOf("lucashealth.ai") === -1;
      });
    } catch (e) {
      console.warn(e);
      toast.error(t("hooks.useEHR.extensionNotAvailable"));
      return [];
    }
  };

  const selectTemplate = (template) => {
    startEHRSync(executionChatRef.current, template);
    setTemplates(null);
  };

  const restart = () => {
    startEHRSync(executionChatRef.current, template.current);
  };

  const reset = () => {
    setDidRun(false);
    setError();
    setErrors([]);
    setExecuting(false);
    setSmartPasteQueue(null);
    failedSections.current = [];
  };

  // Drop Chrome's "is debugging this browser" banner from the EHR tab. Only the normal
  // completion path used to do this, so a stopped or aborted sync left the banner up on
  // the clinic's chart until they closed the tab.
  const detachCdpQuietly = () => {
    try {
      lucasIntegrationRequest({ action: "detachCDP" }).catch(() => {});
    } catch (e) {
      /* extension unavailable — nothing to detach */
    }
  };

  const stopSync = () => {
    cancel.current = true;
    setExecuting(false);
    detachCdpQuietly();
    setError("Synchronization stopped by user");
    // Collect ALL sections for Smart Paste — after manual stop, sync state is uncertain
    if (template.current && template.current.actions) {
      for (let i = 0; i < template.current.actions.length; i++) {
        const a = template.current.actions[i];
        if (a.type === "section") {
          const content = computeSectionContent(i);
          if (content) {
            queueFailedSection(a.value, content, i, "user_stopped");
          }
        }
      }
      if (failedSections.current.length > 0) {
        setSmartPasteQueue([...failedSections.current]);
      }
    }
  };

  const checkActiveTab = async (ignoreChat, reuseLastTab) => {
    return new Promise(async (resolve) => {
      let tabs = await requestTabs();
      if (compareVersions(version, "1.1.0") >= 0) {
        let activeTab = tabs.find((t) => t.current)?.id;
        if (activeTab) {
          // if the is anctive tab (sidepanel mode)
          setExecutionTab(activeTab);
          if (ignoreChat) {
            return resolve(true);
          } else {
            setShowModalSelectTab({ ignoreChat });
          }
        } else {
          if (reuseLastTab && executionTabRef.current) {
            let activeTab = tabs.find((t) => t.id === parseInt(executionTabRef.current));

            if (activeTab) {
              return resolve(true);
            }
          }

          selectedTabPromise.current = resolve;
          setExecutionTab(pickDefaultExecutionTab(tabs, executionTabRef.current));
          setShowModalSelectTab({ ignoreChat });
        }
      } else {
        // Old version skip the active tab check
        return resolve(true);
      }
    });
  };

  const sendMessageToExtension = async (action, message) => {
    try {
      if (!window?.chrome?.runtime?.sendMessage) {
        toast.error(t("hooks.useEHR.chromeExtensionNotAvailable"));
        return;
      }

      return new Promise((resolve, reject) => {
        // console.log("Sending message to extension " + extensionID, { action, ...message });

        window?.chrome.runtime.sendMessage(
          extensionID,
          { request: { action, tab: executionTabRef.current, ...message } },
          function (response) {
            if (window?.chrome.runtime.lastError) {
              reject(window?.chrome.runtime.lastError || "Unknown error");
            } else {
              if (response?.error) {
                console.warn("ERROR Extension - ACTION:" + action + " ERROR: " + response.error);
              }
              resolve(response);
            }
          }
        );
      });
    } catch (e) {
      console.error("Error sending message to extension", e);
    }
  };

  // ------------------
  // ---- Actions -----
  // ------------------

  const magicCopy = async (copyQueue) => {
    if (copyQueue[0] == null) return;
    navigator.clipboard.write([
      new ClipboardItem({
        "text/plain": new Blob([copyQueue[0].text], { type: "text/plain" }),
        "text/html": new Blob([copyQueue[0].html], { type: "text/html" }),
      }),
    ]);
    await sendMessageToExtension("loadCopyQueue", { copyQueue });
  };

  const getCopyQueueRemaining = async () => {
    try {
      let response = await sendMessageToExtension("getCopyQueueStatus");
      return response?.remaining ?? null;
    } catch {
      return null;
    }
  };

  const getScreenshot = async () => {
    let activeTab = await checkActiveTab(true);
    if (activeTab) {
      let data = await sendMessageToExtension("getScreenshot");
      return data;
    }
  };

  const getFieldValue = async (fields) => {
    try {
      const response = await lucasIntegrationRequest({ action: "getLastFocusElement", fields: fields });
      if (response == null) {
        toast.error(t("hooks.useEHR.noElementSelected"));
        return null;
      }
      return response;
    } catch (e) {
      toast.error(t("hooks.useEHR.noElementSelected"));
      return null;
    }
  };

  const highlightFields = async (fields) => {
    let activeTab = await checkActiveTab(true);
    if (activeTab) {
      let data = await sendMessageToExtension("highlightFields", { fields });
      if (!data || data.error) {
        toast.error(t("hooks.useEHR.errorFindingFields"));
      } else {
        if (data.length === 0) {
          toast.warn(t("hooks.useEHR.fieldNotFound"));
        } else {
          toast.success(t("hooks.useEHR.fieldsHighlighted"));
        }
      }
    }
  };

  const focusActiveTab = async () => {
    try {
      const response = await lucasIntegrationRequest({ action: "focusTab" });
      if (response == null) {
        toast.error(t("hooks.useEHR.cannotFindActiveTab"));
      }
    } catch (e) {
      toast.error(t("hooks.useEHR.cannotFindActiveTab"));
    }
  };

  const syncEHR = async (chat) => {
    setExecutionChat(chat);
    setLoadingTemplates(true);
    // try/finally so loadingTemplates always resets — without this, a network
    // error on getEhrTemplates leaves the flag stuck true, which now also
    // permanently disables the Sync EHR button (it's gated on loadingTemplates).
    try {
      let templates = await api.getEhrTemplates();

      templates = templates.data.result.filter((item) => {
        if (item.encounterType !== chat.contextVariables["local:encounterType"]) return false;
        if (item.system !== auth.user.custom.integrationEHR) return false;
        return true;
      });

      if (templates.length === 0) {
        toast.error(
          t("hooks.useEHR.noEhrTemplatesFound", {
            encounterType: chat.contextVariables["local:encounterType"],
            ehrSystem: auth.user.custom.integrationEHR,
          })
        );
      } else if (templates.length === 1) {
        startEHRSync(chat, templates[0]);
      } else {
        setTemplates(templates);
      }
    } finally {
      setLoadingTemplates(false);
    }
  };

  // ----------------
  // ---- Import ----
  // ----------------

  const importEncounterFromEHR = async () => {
    if (version == null) {
      toast.error(t("hooks.useEHR.extensionNotAvailableInstall"));
      return;
    }

    if (auth.user.custom.integrationEHR === "eclinicalworks") {
      //let xpath = ;
      let elements = [
        {
          xpath: "//*[@class='fc-event-container']",
          property: "encounters",
        },
        {
          xpath: '//*[@id="open"]/div[3]/div[2]/table',
          property: "officeVisitList",
        },
      ];

      try {
        const response = await lucasIntegrationRequest({ action: "getAngularScope", elements });
        if (response == null) return;

        if (response.scope == null) {
          globalNotify("No encounters found", "please open the schedule on the EHR", "warning");
          toast.error(t("hooks.useEHR.noEncountersFound"));
          return;
        }
        setImportingEncounters(true);

        try {
          let lucasImportedEncounters = [];

          for (let encounter of response.scope) {
            let patientFirstName = null;
            let patientLastName = null;
            const gender = encounter.sex != null ? encounter.sex.charAt(0) : "";
            const generalNotes = encounter.generalNotes != null ? encounter.generalNotes : "";
            let visitTime = null;
            let visitType = null;

            if (encounter.patName != null) {
              patientFirstName = capitalize(encounter.patName?.split(",")[1]?.trim());
              patientLastName = capitalize(encounter.patName?.split(",")[0]?.trim());
              visitTime = encounter.start != null ? moment(encounter.start, "YYYY-MM-DD HH:mm:ss").valueOf() : null;
              visitType = encounter.visitId;
            } else if (encounter.name != null) {
              patientFirstName = capitalize(encounter.name?.split(",")[1]?.trim());
              patientLastName = capitalize(encounter.name?.split(",")[0]?.trim());
              visitTime = encounter.viewApptTime != null ? moment(encounter.viewApptTime, "h:mm A").valueOf() : null;
              visitType = encounter.visitType;
            } else continue;

            lucasImportedEncounters.push({
              patientFirstName: patientFirstName,
              patientLastName: patientLastName,
              dob: encounter.dob,
              sex: gender,
              visitTime: visitTime,
              visitType: visitType,
              notes: generalNotes,
            });
          }

          setImportingEncounters(false);
          publish("importSchedule", lucasImportedEncounters || []);
        } catch (e) {
          console.error("Error during import encounter from EHR", e);
          setImportingEncounters(false);
          toast.error(t("hooks.useEHR.errorDuringImport"));
        }
      } catch (e) {
        console.error("Error during import encounter from EHR", e);
        setImportingEncounters(false);
        toast.error(t("hooks.useEHR.errorDuringImport"));
      }
    } else {
      toast.error(t("hooks.useEHR.importNotSupported", { ehrSystem: auth.user.custom.integrationEHR }));
    }
  };

  // --------------
  // ---- Sync ----
  // --------------

  // Per-Set opt-in: a Set whose useCopyAll toggle is on bypasses [[ ]] script
  // evaluation and uses the Copy All formatter directly. Default OFF — admins
  // must enable it explicitly per Set.
  const resolveCopyAllValue = (action) => {
    if (!action || action.useCopyAll !== true) return null;
    const noteEl = document.querySelector(".generated-note");
    if (!noteEl) {
      console.warn(
        "EHR Sync: Use Copy All toggle is ON but '.generated-note' is not in the DOM. " +
          "The chat-edit page must be open (note rendered) when triggering sync. " +
          "Returning empty string."
      );
      return "";
    }
    return getCopyAllText(noteEl.innerHTML);
  };

  const startEHRSync = async (chat, selectedTemplate, reuseLastTab, testing) => {
    // Defensive re-gate at the EMR boundary: ingestion (refreshChat) already strips
    // wrong-sex ICD-10 codes, but re-run here so nothing incompatible can reach the EHR
    // even if this chat was synced via a path that bypassed refreshChat. Idempotent —
    // a no-op when the list is already clean.
    applyIcd10SexGate(chat);
    setExecutionChat(chat);

    try {
      if (!window?.chrome?.runtime?.sendMessage) {
        toast.error(t("hooks.useEHR.chromeExtensionNotAvailable"));
        return;
      }
    } catch (e) {
      toast.error(t("hooks.useEHR.chromeExtensionNotAvailable"));
      return;
    }

    let activeTab = await checkActiveTab(chat, reuseLastTab);
    if (!activeTab) {
      return;
    }

    if (!testing && !chat.contextVariables["local:userEHRSyncOn"]) {
      updateContext("userEHRSyncOn", new Date());
    }

    setDidRun(true);

    if (selectedTemplate.configuration != null)
      try {
        selectedTemplate.configuration = JSON.parse(selectedTemplate.configuration);
      } catch (e) {
        console.error("Error parsing EHR template configuration", e);
        toast.error(t("hooks.useEHR.errorParsingTemplateConfig"));
      }

    if (!selectedTemplate.actions) {
      toast.error(t("hooks.useEHR.templateNoActions"));
      return "This template has no actions, please edit it in the settings page.";
    }

    // PREPARE THE ACTIONS
    setShowModalSyncEhr(true);

    currentSection.current = "";
    template.current = selectedTemplate;
    console.log(
      'EHR Sync: using template "' +
        (selectedTemplate.name || "unnamed") +
        '" (id ' +
        selectedTemplate.id +
        ', encounterType "' +
        (selectedTemplate.encounterType || "") +
        '", ' +
        selectedTemplate.actions.length +
        " actions)"
    );
    cancel.current = false;

    setErrors([]);
    errors.length = 0;
    syncReportActions.current = [];
    syncMissedCodes.current = [];
    syncSubstitutedCodes.current = [];
    // Reset chrome-ext CDP user-dismiss flag for this sync (was set true if user dismissed banner in a prior sync)
    // Best-effort — extension may be unavailable; failure is non-fatal.
    try {
      lucasIntegrationRequest({ action: "resetSyncCdpFlags" }).catch(() => {});
    } catch (e) {
      /* extension not available — no-op */
    }
    failedSections.current = [];
    setSmartPasteQueue(null);
    syncStartTime.current = Date.now();
    isTestSync.current = !!testing;
    setExecuting(true);
    setError(null);
    setTotalStep(selectedTemplate.actions.length);

    setTimeout(async () => {
      try {
        // Pre-cache resolved values for Smart Paste fallback
        // If an event/click fails before set actions run, computeSectionContent needs cached values
        for (const a of selectedTemplate.actions) {
          // The new dynamic-loop actions resolve their values at execution time, not here.
          if (a.type === "pasteAndPickList" || a.type === "pasteSearchPick") continue;

          if ((a.type === "set" || a.type === "merge") && (a.value || a.useCopyAll)) {
            try {
              const copyAllResolved = resolveCopyAllValue(a);
              a._resolvedValue = copyAllResolved !== null ? copyAllResolved : lucasIntegrationGetContent(a.value, 0, a);
            } catch (e) {
              // Ignore resolution errors — will retry during execution
            }
          }
        }
        await executeAction(0);
      } catch (e) {
        console.error("Unexpected error during EHR sync", e);
        setExecuting(false);
        setError("Generic Error");
      }
    }, 100);

    return true;
  };

  async function lucasIntegrationRequest(message) {
    try {
      if (window?.chrome === null || window?.chrome.runtime === null || version === null) {
        toast.error(t("hooks.useEHR.chromeExtensionNotAvailable"));
        console.error("LucasAI integration: chrome not available");
        queueAllRemainingSections("chrome_unavailable");
        throw new Error("Chrome Extension not available");
      }

      return new Promise((resolve, reject) => {
        window?.chrome.runtime.sendMessage(
          extensionID,
          { request: { ...message, tab: executionTabRef.current } },
          function (response) {
            if (!window?.chrome.runtime.lastError) {
              if (response?.error === "Could not establish connection. Receiving end does not exist.") {
                setError("No open tab found for the EHR, please open your EHR in a browser tab");
                setExecuting(false);
                queueAllRemainingSections("no_ehr_tab");
                reject(new Error("No open tab found for the EHR"));
                return;
              }

              resolve(response);
            } else {
              console.log("EHR Sync: error on sending message to extension", window?.chrome.runtime.lastError);
              globalNotify(
                "The Encounter 4",
                "has not been sent to the EHR. Please assure to have the Chrome plugin installed and your EHR open next to {{server:presentation.productName}}",
                "warning",
                5000
              );
              cancel.current = true;
              queueAllRemainingSections("extension_disconnected");
              reject(window?.chrome.runtime.lastError);
            }
          }
        );
      });
    } catch (e) {
      console.log("EHR Sync: exception during sendMessage", e);
      throw e;
    }
  }

  const executeAction = async (currentIndex, currentRetry, lastError) => {
    if (cancel.current) {
      console.log("Synchronization to EHR stopped by user");
      return;
    }

    if (!currentRetry) currentRetry = 0;

    setCurrentStep(currentIndex + 1);

    console.log(
      "EHR Sync: Synch with EHR, executing action " +
        (currentIndex + 1) +
        "/" +
        template.current.actions.length +
        " retry " +
        currentRetry
    );

    if (errors.length >= MAX_ERRORS_BEFORE_ABORT) {
      console.log("EHR Sync: completed with " + errors.length + " errors");

      lucasIntegrationSendSyncError();

      globalNotify(
        "The Encounter 1",
        "has not been sent to the EHR. Please assure to have the Chrome plugin installed and your EHR open next to {{server:presentation.productName}}",
        "warning",
        5000
      );

      // Collect ALL sections for Smart Paste (failed sections had errors, remaining were never attempted)
      for (let i = 0; i < template.current.actions.length; i++) {
        const a = template.current.actions[i];
        if (a.type === "section") {
          const content = computeSectionContent(i);
          if (content) {
            queueFailedSection(a.value, content, i, "sync_aborted");
          }
        }
      }

      setExecuting(false);
      if (failedSections.current.length > 0) {
        setSmartPasteQueue([...failedSections.current]);
      }
      setError("Too many errors during synchronization");
      return;
    }

    if (currentIndex >= template.current.actions.length) {
      // Helper: surface auto-mapped (parent → unspecified child) substitutions so the
      // provider can review them in the chart. These are SUCCESSFUL commits but with a
      // code different from what the AI suggested — silent acceptance would be a
      // billing-accuracy risk, so we always notify.
      const notifySubstitutedCodes = () => {
        if (!syncSubstitutedCodes.current || syncSubstitutedCodes.current.length === 0) return;
        const lines = syncSubstitutedCodes.current
          .map((s) => `${s.originalCode} → ${s.code}${s.description ? ` (${s.description})` : ""}`)
          .join("; ");
        globalNotify(
          "The Encounter — codes auto-mapped",
          `${syncSubstitutedCodes.current.length} non-billable parent code(s) replaced with the unspecified child variant. Review in chart: ${lines}`,
          "warning",
          10000
        );
      };

      // Helper: surface missed-codes warning if any (used by both success and error paths)
      const notifyMissedCodes = () => {
        if (!syncMissedCodes.current || syncMissedCodes.current.length === 0) return;
        // commit_removed_existing_code is the one miss where the automation has POSITIVE
        // knowledge a previously-selected diagnosis was DE-selected during the commit.
        // "Codes not found" is the wrong headline for that — the right provider action is
        // "open the chart and verify", not "add it manually" — so it gets its own toast
        // and is excluded from the generic not-found list below.
        const removals = syncMissedCodes.current.filter((m) => m.reason === "commit_removed_existing_code");
        const notBilled = syncMissedCodes.current.filter((m) => m.reason === NOT_BILLED_MISS_REASON);
        if (notBilled.length > 0) globalNotify("The Encounter — not billed", formatNotBilledNotice(notBilled), "info", 8000);
        const notFound = syncMissedCodes.current.filter(
          (m) => m.reason !== "commit_removed_existing_code" && m.reason !== NOT_BILLED_MISS_REASON
        );
        // SF-3: show "requested→substituted" when an auto-mapped commit later failed, so the
        // provider sees the code they actually asked for, not just the child code that was tried.
        const attemptedLabel = (m) => (m.originalCode ? `${m.originalCode}→${m.code}` : m.code);
        if (removals.length > 0) {
          const removalLines = removals
            .map((m) => `while committing ${attemptedLabel(m)}, removed: ${(m.removedCodes || []).join(", ") || "unknown"}`)
            .join("; ");
          globalNotify(
            "The Encounter — a diagnosis may have been removed",
            `Previously-selected code(s) were de-selected during sync (${removalLines}). ` +
              `Open the chart and verify BOTH the removed and the attempted code(s) before signing.`,
            "warning",
            12000
          );
        }
        if (notFound.length === 0) return;
        const typeCounts = notFound.reduce((acc, m) => {
          acc[m.type] = (acc[m.type] || 0) + 1;
          return acc;
        }, {});
        const reasonCounts = notFound.reduce((acc, m) => {
          const r = m.reason || "unknown";
          acc[r] = (acc[r] || 0) + 1;
          return acc;
        }, {});
        const typeStr = Object.entries(typeCounts)
          .map(([t, n]) => `${n} ${t}`)
          .join(", ");
        const reasonStr = Object.entries(reasonCounts)
          .map(([r, n]) => `${r}: ${n}`)
          .join(", ");
        // SF-3: show "requested→substituted" when an auto-mapped commit later failed, so the
        // provider sees the code they actually asked for, not just the child code that was tried.
        const missedList = notFound.map((m) => (m.originalCode ? `${m.originalCode}→${m.code}` : m.code)).join(", ");
        // Reason counts are critical for support triage — without them, "3 codes missed" is unactionable.
        globalNotify(
          "The Encounter — codes not found",
          `${typeStr} code(s) not found in EHR. Reasons: ${reasonStr}. Codes: ${missedList}`,
          "warning",
          8000
        );
      };

      // Detach CDP debugger if it was attached during this sync
      try {
        await lucasIntegrationRequest({ action: "detachCDP" });
      } catch (_) {}

      publish("ehrSyncingCompleted", {});

      // --- Phase 2: Sync Report + Alerting ---
      finalizeSyncReport("complete");
      // --- End Phase 2 ---

      if (errors.length > 0) {
        console.log("EHR Sync: completed with " + errors.length + " errors");

        // // SAVE THE EHR SYNC EVENT FOR STATISTICS
        // lucasUpdateSection("error", "userEHRSyncResult");

        lucasIntegrationSendSyncError();

        if (errors.length >= MAX_ERRORS_BEFORE_ABORT) {
          globalNotify(
            "The Encounter 2",
            "has not been sent to the EHR. Please assure to have the Chrome plugin installed and your EHR open next to {{server:presentation.productName}}",
            "warning",
            5000
          );
          notifyMissedCodes();
          notifySubstitutedCodes();
        } else {
          setError("Completed with errors, please check the log.");
          setExecuting(false);
          if (failedSections.current.length > 0) {
            setSmartPasteQueue([...failedSections.current]);
          }
          notifyMissedCodes();
          notifySubstitutedCodes();
        }

        return;
      }

      console.log("EHR Sync: Synch with EHR, encounter synchronized correctly");
      setExecuting(false);
      notifyMissedCodes(); // no-op when the report is empty
      if (hasActionableMisses(syncMissedCodes.current)) {
        notifySubstitutedCodes();
      } else if (syncSubstitutedCodes.current && syncSubstitutedCodes.current.length > 0) {
        // No misses but at least one substitution — surface the substitution warning
        // instead of the generic success toast so the provider knows to review.
        notifySubstitutedCodes();
      } else {
        globalNotify("The Encounter", "has been sent to the EHR.", "success");
      }

      // Progressive enrichment: persist any fingerprints captured during execution
      try {
        if (template.current?.id) {
          let enriched = false;
          for (let i = 0; i < template.current.actions.length; i++) {
            const a = template.current.actions[i];
            if (a._capturedFingerprint && (!a.fingerprint || a.fingerprint.hash !== a._capturedFingerprint.hash)) {
              a.fingerprint = a._capturedFingerprint;
              enriched = true;
            }
            delete a._capturedFingerprint;
          }
          if (enriched && !isTestSync.current) {
            api.saveEhrTemplate(template.current.id, template.current);
            console.log("LucasAI Resolver: enriched template with captured fingerprints");
          }
        }
      } catch (e) {
        // Non-critical — fingerprint enrichment is best-effort
        console.warn("LucasAI Resolver: fingerprint enrichment failed:", e.message);
      }

      return;
    }

    const action = template.current.actions[currentIndex];

    // Per-action enable/disable. Default behavior is "enabled" — only false explicitly skips.
    // Used by data-mappers to temporarily disable an action without removing it from the template.
    if (action?.enabled === false) {
      recordActionResult(
        action,
        currentIndex,
        currentSection.current,
        makeActionResult({ status: "skipped", reason: "disabled_by_template" })
      );
      await executeAction(currentIndex + 1);
      return;
    }

    publish("ehrSyncing", { actionType: action?.type, index: action?.index ?? currentIndex });

    if (currentRetry >= maxRetries) {
      if (errors.length >= MAX_ERRORS_BEFORE_ABORT) {
        console.log("EHR Sync: encounter sync aborted (" + errors.length + " errors)");
        globalNotify(
          "The Encounter 3",
          "has not been sent to the EHR. Please assure to have the Chrome plugin installed and your EHR open next to {{server:presentation.productName}}",
          "warning",
          5000
        );
        cancel.current = true;

        // Mark remaining actions as skipped in sync report
        for (let i = currentIndex; i < template.current.actions.length; i++) {
          recordActionResult(
            template.current.actions[i],
            i,
            currentSection.current,
            makeActionResult({ strategy: "none", confidence: 0, status: "skipped" })
          );
        }

        // Collect ALL sections for Smart Paste (failed + remaining)
        for (let i = 0; i < template.current.actions.length; i++) {
          const a = template.current.actions[i];
          if (a.type === "section") {
            const content = computeSectionContent(i);
            if (content) {
              queueFailedSection(a.value, content, i, "sync_aborted");
            }
          }
        }

        // --- Phase 2: Sync Report on abort ---
        finalizeSyncReport("aborted");
        // --- End Phase 2 ---

        detachCdpQuietly();

        // Note: setExecuting(false) was missing in the original abort path (pre-existing bug fix)
        setExecuting(false);
        if (failedSections.current.length > 0) {
          setSmartPasteQueue([...failedSections.current]);
        }

        return;
      }

      console.log("EHR Sync: too many retries, skip this (total errors=" + errors.length + ", lastError=" + lastError + ")");

      // Rich text verify retries are false positives — the set succeeded, readback is just unreliable
      const isRichTextFalsePositive = lastError === "rich_text_verify_retry";
      if (!isRichTextFalsePositive) {
        lucasIntegrationSyncError(currentIndex, action, lastError != null ? lastError : "Too many retries, skip this");
      }

      // Queue full section content for Smart Paste (any action type — click/event failures included)
      // Skip queueing for rich text verify retries — value was written, editor iframe just couldn't confirm
      if (!isRichTextFalsePositive) {
        const queueOpts = {};
        if (action.type === "click" || action.type === "event") {
          queueOpts.panelHint = "Open the " + (currentSection.current || "section") + " panel first";
        }
        if (action.elementType) {
          queueOpts.elementType = action.elementType;
          queueOpts.element = action.element;
        }
        queueFullSectionContent(currentSection.current, currentIndex, "retries_exhausted", queueOpts);
      }

      await executeAction(currentIndex + 1);
      return;
    }

    try {
      switch (action?.type) {
        case "section":
          // FIND THE NEXT SECTION
          let nextSectionIndex = currentIndex + 1;
          for (; nextSectionIndex < template.current.actions.length; ++nextSectionIndex) {
            const nextAction = template.current.actions[nextSectionIndex];
            if (nextAction.type === "section") break;
          }

          if (action.conditionType === "nempty") {
            if (lucasIntegrationGetContent(action.conditionValue, currentIndex, action).trim() === "") {
              console.log("EHR Sync: skipping section at index " + currentIndex + " because condition is not met");
              recordActionResult(action, currentIndex, currentSection.current, makeActionResult({ status: "skipped" }));
              await executeAction(nextSectionIndex);
              return;
            }
          } else if (action.conditionType === "js") {
            try {
              // eslint-disable-next-line no-new-func
              const result = new Function("note", lucasIntegrationGetContent(action.conditionValue, currentIndex, action))(
                executionChatRef.current
              );
              if (!result) {
                recordActionResult(action, currentIndex, currentSection.current, makeActionResult({ status: "skipped" }));
                await executeAction(nextSectionIndex);
                return;
              }
            } catch (e) {
              let expression = "test";
              console.log("EHR Sync: Error on evaluating section condition js  '" + expression + "'", e);
              lucasIntegrationSyncError(
                currentIndex,
                action,
                "Error on evaluating section condition js expression '" + expression + "'"
              );
              await executeAction(nextSectionIndex);
              return;
            }
          }

          currentSection.current = action.value;

          recordActionResult(action, currentIndex, currentSection.current, makeActionResult());
          await executeAction(currentIndex + 1);
          break;

        case "set": {
          try {
            const response = await lucasIntegrationRequest({
              action: "getFieldValue",
              fields: [{ elementType: action.elementType, element: action.element }],
            });

            if (response === null) {
              console.log("EHR Sync: value not found, retrying (" + (currentRetry + 1) + "/" + maxRetries + ")");
              setTimeout(
                () => {
                  executeAction(currentIndex, currentRetry + 1, "value not found before setting");
                },
                500 + 100 * currentRetry
              );
            } else {
              let value = action.value;
              const copyAllResolved = resolveCopyAllValue(action);
              if (copyAllResolved !== null) {
                value = copyAllResolved;
              } else if (action.elementType === "tinymce" || action.elementType === "wysihtml" || action.useHTML) {
                // COPY HTML
                value = lucasIntegrationGetContent(value, currentIndex, action);
              } else {
                // COPY TEXT FROM TEXT OR HTML
                const div = document.createElement("div");
                div.innerHTML = DOMPurify.sanitize(lucasIntegrationGetContent(value, currentIndex, action)); // nosemgrep: insecure-document-method
                value = doctorElemToText("", div, -2, false);
                if (value.endsWith("\n")) value = value.substring(0, value.length - 1);
                div.remove();
              }

              // Cache resolved value for Smart Paste fallback (fix #4: physicalExams)
              action._resolvedValue = value;

              if (template.current.system === "eclinicalworks") value = lucasConvertToBasicChars(value);

              // Opt-in: when the resolved value is empty (e.g. insurance mapping did not
              // produce a match), skip the dispatch entirely — never clear an EHR field.
              if (action.skipIfEmpty && (!value || !String(value).trim())) {
                console.log("EHR Sync: empty value, skipping set (skipIfEmpty)");
                recordActionResult(
                  action,
                  currentIndex,
                  currentSection.current,
                  makeActionResult({ status: "skipped", reason: "empty_value" })
                );
                await executeAction(currentIndex + 1);
                break;
              }

              // Opt-in: translate a code/display value into the exact <option> value by
              // reading the live <select> through the shipped getDOM primitive — the
              // shipped extension only matches option values, never option text, so the
              // webapp does the text matching and sends the exact value attribute.
              if (action.resolveSelectOption) {
                const normText = (s) =>
                  String(s || "")
                    .toUpperCase()
                    .replace(/[^A-Z0-9]+/g, " ")
                    .trim();
                let optionValue = "";
                try {
                  const domResp = await lucasIntegrationRequest({
                    action: "getDOM",
                    root: { elementType: action.elementType, element: action.element },
                  });
                  const html = Array.isArray(domResp) ? (domResp.find((r) => r && r.html) || {}).html : domResp && domResp.html;
                  const doc = new DOMParser().parseFromString(html || "", "text/html");
                  const options = [...doc.querySelectorAll("option")]
                    .map((o) => ({ value: o.getAttribute("value") || "", text: (o.textContent || "").trim() }))
                    .filter((o) => o.value !== "");
                  const target = String(value).trim();
                  const tiers = [
                    (o) => o.value === target,
                    (o) => o.text === target,
                    (o) => o.text.toUpperCase() === target.toUpperCase(),
                    (o) => o.text.toUpperCase().startsWith(target.toUpperCase() + " - "),
                    (o) => normText(o.text) === normText(target),
                  ];
                  for (const match of tiers) {
                    const hit = options.find(match);
                    if (hit) {
                      optionValue = hit.value;
                      break;
                    }
                  }
                  if (!optionValue && normText(target) !== "") {
                    // Last resort: unique prefix in either direction — AdvancedMD facility
                    // names often drop the entity suffix (option "HOOVER FAMILY MEDICINE"
                    // vs configured "Hoover Family Medicine PA").
                    const tNorm = normText(target);
                    const prefixHits = options.filter((o) => {
                      const oNorm = normText(o.text);
                      return oNorm !== "" && (oNorm.startsWith(tNorm) || tNorm.startsWith(oNorm));
                    });
                    if (prefixHits.length === 1) optionValue = prefixHits[0].value;
                  }
                } catch (e) {
                  // getDOM unavailable or select missing — treated as not found below.
                }
                if (!optionValue) {
                  console.log('EHR Sync: no <option> matched "' + value + '", skipping set');
                  recordActionResult(
                    action,
                    currentIndex,
                    currentSection.current,
                    makeActionResult({ status: "skipped", reason: "option_not_found" })
                  );
                  await executeAction(currentIndex + 1);
                  break;
                }
                value = optionValue;
              }

              // Phase 3: Confidence safeguard
              if (shouldSkipByConfidence(action)) {
                recordActionResult(
                  action,
                  currentIndex,
                  currentSection.current,
                  makeActionResult({
                    strategy: action._resolverStrategy || "none",
                    confidence: action._resolverConfidence || 0,
                    status: "failed",
                  })
                );
                queueFullSectionContent(currentSection.current, currentIndex, "confidence_low");
                lucasIntegrationSyncError(currentIndex, action, "Confidence too low, skipped for safety");
                await executeAction(currentIndex + 1);
                break;
              }

              // Phase 3: Field type validation
              if (
                shouldSkipByFieldType(action, {
                  elementTag: action._resolvedElementTag,
                  elementInputType: action._resolvedElementInputType,
                })
              ) {
                recordActionResult(
                  action,
                  currentIndex,
                  currentSection.current,
                  makeActionResult({
                    strategy: action._resolverStrategy || "none",
                    confidence: action._resolverConfidence || 0,
                    status: "failed",
                  })
                );
                queueFullSectionContent(currentSection.current, currentIndex, "wrong_field_type");
                lucasIntegrationSyncError(currentIndex, action, "Wrong field type, skipped for safety");
                await executeAction(currentIndex + 1);
                break;
              }

              const setResult = await lucasIntegrationSetValue(action, value, currentIndex, currentRetry);
              if (setResult.success === "ok") {
                recordActionResult(
                  action,
                  currentIndex,
                  currentSection.current,
                  makeActionResult({
                    strategy: action._resolverStrategy || "none",
                    confidence: action._resolverConfidence || 0,
                    status: setResult.verification?.status || "success",
                    verified: setResult.verification?.verified || false,
                    mismatchType: setResult.verification?.mismatchType || null,
                  })
                );
                // Phase 3: Queue for Smart Paste if verification returned empty
                // Skip for rich text fields — readback is unreliable due to editor iframe load timing
                if (setResult.verification?.mismatchType === "empty") {
                  const isRich = ["wysihtml", "tinymce"].includes((action.elementType || "").toLowerCase());
                  if (!isRich) {
                    queueFullSectionContent(currentSection.current, currentIndex, "empty_mismatch");
                  }
                }
                await executeAction(currentIndex + 1);
              } else if (setResult.success === "retry") {
                const isRichRetry = ["wysihtml", "tinymce"].includes((action.elementType || "").toLowerCase());
                await executeAction(
                  currentIndex,
                  currentRetry + 1,
                  isRichRetry ? "rich_text_verify_retry" : "Error on setting value"
                );
              } else {
                recordActionResult(
                  action,
                  currentIndex,
                  currentSection.current,
                  makeActionResult({
                    strategy: action._resolverStrategy || "none",
                    confidence: 0,
                    status: "failed",
                  })
                );
                queueFullSectionContent(currentSection.current, currentIndex, "set_error");
                lucasIntegrationSyncError(currentIndex, action, "Error on setting value");
                console.error("EHR Sync: Blocking error: ", setResult.errorMsg);
                setError(setResult.errorMsg);
                setExecuting(false);
                if (failedSections.current.length > 0) {
                  setSmartPasteQueue([...failedSections.current]);
                }
              }
            }
          } catch (e) {
            console.error("Error in set action", e);
            await executeAction(currentIndex, currentRetry + 1, "Error on setting value");
          }
          break;
        }

        case "event": {
          try {
            const isClickIfExists = action.value === "clickIfExists";
            const eventElement = { elementType: action.elementType, element: action.element };
            if (action.fingerprint) eventElement.fingerprint = action.fingerprint;
            const sendEvent = async () => {
              const res = await lucasIntegrationRequest({
                action: "event",
                element: eventElement,
                eventType: isClickIfExists ? "click" : action.value,
                useCDP: action.useCDP || false,
              });
              // Capture fingerprint from resolver for progressive enrichment
              if (res && res._fp) {
                action._capturedFingerprint = res._fp;
              }
              return res;
            };

            // The editor only offers (and gates) the retry fields for plain
            // clicks. A step switched to another event type keeps whatever
            // retry fields it had, so gate here too — never re-dispatch an
            // input/blur/keyup N times because a stale retryClicks survived.
            const clickPolicy = !isClickIfExists && action.value === "click" ? resolveClickRetryPolicy(action) : null;
            const verifyClick = !!(clickPolicy && clickPolicy.verifySelector);
            let clickRun = null;
            let response;
            if (verifyClick) {
              clickRun = await runVerifiedClick({
                policy: clickPolicy,
                click: sendEvent,
                getTargetState: async () => {
                  // Probe the target with the SAME descriptor the click uses,
                  // fingerprint included: a target the click only resolves by
                  // fingerprint (xpath drift) would otherwise probe as hidden
                  // and end the loop before any re-click or escalation.
                  const targetProbe = { elementType: action.elementType, element: action.element, visibleOnly: true };
                  if (action.fingerprint) targetProbe.fingerprint = action.fingerprint;
                  const visible = interpretProbe(
                    await lucasIntegrationRequest({
                      action: "existsElement",
                      field: targetProbe,
                    })
                  );
                  let disabled = null;
                  if (clickPolicy.disabledSelector) {
                    disabled = interpretProbe(
                      await lucasIntegrationRequest({
                        action: "existsElement",
                        field: {
                          elementType: clickPolicy.verifyElementType,
                          element: clickPolicy.disabledSelector,
                          visibleOnly: true,
                        },
                      })
                    );
                  }
                  return { visible, disabled };
                },
                pollForElement: (ms) => pollForElement(clickPolicy.verifySelector, ms, clickPolicy.verifyElementType),
                isCancelled: () => cancel.current,
                log: (msg) => console.log(msg),
              });
              if (clickRun.stoppedReason === "cancelled") {
                // The user pressed Stop mid-loop. Not a failed step: record
                // nothing, raise no sync error, advance nowhere — the cancel
                // guard at the top of executeAction owns the shutdown.
                console.log("EHR Sync: click step stopped by user");
                break;
              }
              response = clickRun.stoppedReason === "target_missing" ? null : true;
            } else {
              response = await sendEvent();
            }

            if (response === null && !isClickIfExists) {
              console.log("EHR Sync: element not found, retrying (" + (currentRetry + 1) + "/" + maxRetries + ")");
              setTimeout(
                () => {
                  executeAction(currentIndex, currentRetry + 1, "Element not found");
                },
                500 + 100 * currentRetry
              );
            } else {
              // Optional postcondition: some EHR controls (e.g. AdvancedMD's "Add Linked →
              // Charge Slip - New" toolbar) swallow untrusted clicks — the dispatch
              // "succeeds" but the UI never changes, and the sync used to report a phantom
              // success while the charge slip never appeared. When the template declares
              // verifySelector, the click only counts once that element shows up.
              //
              // A missing postcondition is INDISTINGUISHABLE from "the click landed and
              // the EHR is slow", and these buttons create billing records — so the
              // failure path never re-clicks on its own. Re-clicking is opt-in per action
              // (retryClicks, allowCdpEscalation) for controls a data-mapper has confirmed
              // are safe to press again; runVerifiedClick stops re-clicking as soon as the
              // target is greyed out or gone, and the final window is generous so a slow
              // render doesn't look like a failure.
              if (verifyClick) {
                let postconditionOk = clickRun.ok;
                let clickAttempts = clickRun.attempts;
                if (
                  !postconditionOk &&
                  clickRun.stoppedReason === "attempts_exhausted" &&
                  clickPolicy.allowCdpEscalation &&
                  !action.useCDP &&
                  action.value === "click" &&
                  !cancel.current
                ) {
                  console.log("EHR Sync: postcondition missing after click — escalating to CDP trusted click (opt-in)");
                  const cdpResponse = await lucasIntegrationRequest({
                    action: "event",
                    element: eventElement,
                    eventType: "click",
                    useCDP: true,
                  });
                  if (cdpResponse && !cdpResponse.error) {
                    clickAttempts++;
                    postconditionOk = await pollForElement(
                      clickPolicy.verifySelector,
                      clickPolicy.verifyTimeoutMs,
                      clickPolicy.verifyElementType
                    );
                  }
                }
                if (!postconditionOk) {
                  // Visible, once — never a silent success.
                  console.log("EHR Sync: postcondition never appeared after click at index " + currentIndex);
                  const isDisabled = clickRun.stoppedReason === "target_disabled";
                  recordActionResult(
                    action,
                    currentIndex,
                    currentSection.current,
                    makeActionResult({
                      status: "failed",
                      reason: isDisabled ? "target_disabled" : "postcondition_not_met",
                      attempts: clickAttempts,
                    })
                  );
                  lucasIntegrationSyncError(
                    currentIndex,
                    action,
                    isDisabled
                      ? '"' +
                          String(action.description || "This button").replace(
                            /[&<>"]/g,
                            (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]
                          ) +
                          '" is greyed out in the EHR, so the click could not open it. Check that the note is saved and that the item is not already created, then retry.'
                      : "Clicked " +
                          clickAttempts +
                          " time(s), but the expected screen never appeared — the following steps may not have applied"
                  );
                  await executeAction(currentIndex + 1);
                  break;
                }
              }
              const resolverStatus =
                (action._resolverConfidence ?? 1) < 0.85 || action._resolverStrategy === "fingerprint" ? "uncertain" : "success";
              recordActionResult(
                action,
                currentIndex,
                currentSection.current,
                makeActionResult({
                  strategy: action._resolverStrategy || "none",
                  confidence: action._resolverConfidence || 0,
                  status: resolverStatus,
                })
              );
              await executeAction(currentIndex + 1);
            }
          } catch (e) {
            console.error("Error in event action", e);
            if (action.value === "clickIfExists") {
              await executeAction(currentIndex + 1);
            } else {
              await executeAction(currentIndex, currentRetry + 1, "Element not found");
            }
          }
          break;
        }
        case "pasteAndPickList": {
          // Enablement is controlled by the template author (an internal data-mapper). If a template
          // contains pasteAndPickList actions, the data-mapper has decided the customer should run them.
          // The supported-systems guard below is the only system-level safety net.
          if (!isPasteAndPickListSupported(template.current.system)) {
            recordActionResult(
              action,
              currentIndex,
              currentSection.current,
              makeActionResult({ status: "failed", reason: "ehr_not_supported" })
            );
            await executeAction(currentIndex + 1);
            break;
          }

          // Read + sanitize the code list (uses helpers imported at top of file)
          const source = executionChatRef.current.contextVariables["local:" + action.valueSource];
          const { codes, dropped } = sanitizeCodes(source, action.codeProperty || "code", {
            honorNotBilled: false,
            valueSource: action.valueSource,
          });

          if (dropped.empty > 0 || dropped.duplicate > 0 || dropped.wrongSlot > 0 || dropped.invalidTokens.length > 0) {
            console.log(
              `LucasAI pasteAndPickList: sanitized ${codes.length} codes (dropped ${dropped.empty} empty, ${dropped.duplicate} duplicate, ${dropped.wrongSlot} wrong-slot, ${dropped.invalidTokens.length} invalid)`
            );
          }
          // Garbled tokens (e.g. an all-digit jam like "993869921325") must fail
          // VISIBLY: route them into the end-of-sync missed-codes toast rather
          // than silently thinning the list. (syncMissedCodes.current is always
          // initialized by startEHRSync before actions run.)
          for (const tkn of dropped.invalidTokens) {
            syncMissedCodes.current.push({ type: "ICD-10", code: tkn, reason: "invalid_code_format" });
          }

          if (codes.length === 0) {
            recordActionResult(
              action,
              currentIndex,
              currentSection.current,
              makeActionResult({ status: "skipped", reason: "no_codes" })
            );
            await executeAction(currentIndex + 1);
            break;
          }

          // Optional ordering: bump a code family to the front (e.g. prioritizePrefix:"Z"
          // lists Z-codes first). Reorder BEFORE building the paste string + selectors so
          // both stay aligned. Default (no prioritizePrefix) leaves the order unchanged.
          const orderedCodes = action.prioritizePrefix ? prioritizeByPrefix(codes, action.prioritizePrefix) : codes;

          // Version-skew guard, honestly scoped: the webapp deploys instantly, but the
          // extension rolls out through the store over days. `chrome-app/manifest.json` has
          // read "1.1.13" continuously since 2026-05-14 while background.js changed at least
          // five more times under that SAME label — so this compare only guarantees that the
          // extension's generic CDP action layer (`event`/`setFieldValue` with useCDP, plus
          // getDOM/existsElement) EXISTS, not any particular behavior inside it. In
          // particular, both the debugging-banner-dismissal error string pfPickerDriver.js
          // keys on (shipped in commit 0e43931ff) and cdpFindElement's preference for the
          // first VISIBLE match on a duplicated/hidden selector (shipped in commit 813fa1139)
          // landed on 2026-07-30 — under this same "1.1.13" label. A pre-2026-07-30 1.1.13
          // build clears this gate and has neither: pfPickerDriver.js's isDismissedResponse
          // additionally treats a RESOLVED old-build-shaped CDP error (e.g. "CDP error: ...")
          // on a useCDP action as a cancel condition, so a stale build still fails visibly
          // (one cancellation, not N debugger-banner re-pops) — but it does NOT restore the
          // zero-geometry click refusal a pre-July build lacks, which no version comparison
          // here can express. Closing that gap needs a manifest bump + republish (or
          // confirming the fleet's deployed build post-dates 0e43931ff) — a release-process
          // condition, not a code fix. Refuse visibly until the floor is met.
          if (template.current.system === "practicefusion" && compareVersions(version, "1.1.13") < 0) {
            recordActionResult(
              action,
              currentIndex,
              currentSection.current,
              makeActionResult({ status: "failed", reason: "extension_update_required" })
            );
            for (const c of orderedCodes) {
              syncMissedCodes.current.push({ type: "ICD-10", code: c, reason: "extension_update_required" });
            }
            await executeAction(currentIndex + 1);
            break;
          }

          // Selectors and the request plan are built BEFORE the extension try/catch below
          // on purpose: a mis-authored template (missing optionSelectorTemplate) must keep
          // surfacing as the sync's "Generic Error" halt, not as a per-action ext_error row
          // that lets the sync finish green with no diagnosis entered.
          const isPracticeFusion = template.current.system === "practicefusion";
          const pfOptionSelectors = isPracticeFusion
            ? buildIcd10OptionSelectors(orderedCodes, action.optionSelectorTemplate)
            : null;
          const pfVerifySelectors = isPracticeFusion ? buildVerifySelectors(orderedCodes, action.verifySelectorTemplate) : null;
          // AdvancedMD: the note's codes become one comma-paste request per run of
          // batch-safe codes and one bare-paste request per code that would break the
          // comma list (3-character codes like J00, a letter right after the dot, and the
          // substrings in BATCH_UNSAFE_CODE_PATTERNS — see planPasteAndPickListRequests).
          // One such code in the list makes the picker render NONE of them, so it must
          // never share a paste with the others. The note's order is preserved.
          const plan = isPracticeFusion ? null : planPasteAndPickListRequests(orderedCodes, action);
          if (plan) {
            console.log("LucasAI pasteAndPickList: plan " + plan.map((r) => r.mode + "[" + r.codes.join(",") + "]").join(" -> "));
          }

          // Dispatch. Practice Fusion is driven from here, one code at a time, over the
          // extension's shipped generic CDP actions — the extension has no PF engine (and
          // needs no release to ship or hotfix this one). The driver returns the same
          // {matched, missed, cancelled} shape the extension message returns, so everything
          // below is common to both systems.
          let response;
          try {
            if (isPracticeFusion) {
              response = await runPfPasteAndPickList({
                sendRequest: lucasIntegrationRequest,
                codes: orderedCodes,
                optionSelectors: pfOptionSelectors,
                verifySelectors: pfVerifySelectors,
                searchInput: action.searchInput,
                autoMapParents: action.autoMapParents,
                waits: {
                  waitAfterPasteMs: action.waitAfterPasteMs || 1500,
                  waitAfterClickMs: action.waitAfterClickMs || 5000,
                },
              });
            } else {
              // Replies merge into the single shape the reporting below expects. The
              // dispatcher stops on a whole-run error or a cancel (see its doc) and never
              // throws, so the unaccounted-codes sweep below covers whatever it didn't reach.
              response = await dispatchPasteAndPickListPlan({
                plan,
                sendRequest: lucasIntegrationRequest,
                isCancelled: () => cancel.current,
                // One guarded solo re-send for codes the extension never clicked (see the
                // dispatcher). Template authors can switch it off per org.
                retryUnattempted: action.retryUnattempted !== false,
                baseRequest: {
                  action: "pasteAndPickList",
                  ehrSystem: template.current.system,
                  searchInput: action.searchInput,
                  refinementSelector: action.refinementSelector,
                  waitAfterPasteMs: action.waitAfterPasteMs || 1500,
                  waitAfterClickMs: action.waitAfterClickMs || 5000,
                  waitAfterRefinementMs: action.waitAfterRefinementMs || 5000,
                },
              });
            }
          } catch (e) {
            recordActionResult(
              action,
              currentIndex,
              currentSection.current,
              makeActionResult({ status: "failed", reason: "ext_error:" + e.message })
            );
            await executeAction(currentIndex + 1);
            break;
          }

          // Per-code report rows
          const matched = response?.matched || [];
          const missed = response?.missed || [];
          matched.forEach((m) => {
            recordActionResult(
              action,
              currentIndex,
              currentSection.current,
              makeActionResult({ status: "success", strategy: "xpath", verified: true })
            );
            // If the ext auto-mapped a non-billable parent to its unspecified child,
            // accumulate for the substitution toast so the provider knows to review.
            if (m.viaParentAutoPick && m.originalCode) {
              if (!syncSubstitutedCodes.current) syncSubstitutedCodes.current = [];
              syncSubstitutedCodes.current.push({
                type: "ICD-10",
                originalCode: m.originalCode,
                code: m.code,
                description: m.autoPickDescription,
                reason: m.autoPickReason,
              });
            }
          });
          missed.forEach((m) => {
            recordActionResult(
              action,
              currentIndex,
              currentSection.current,
              makeActionResult({ status: "failed", reason: m.reason })
            );
            // Accumulate for end-of-sync toast (do NOT push to errors[] — these don't trip MAX_ERRORS_BEFORE_ABORT)
            if (!syncMissedCodes.current) syncMissedCodes.current = [];
            // SF-3: an auto-mapped commit that then fails reports the CHILD code (m.code) here —
            // carry the originally-requested code too (mirrors the matched-path substitution toast
            // above) so the provider isn't told a code they never requested was missed.
            syncMissedCodes.current.push({
              type: "ICD-10",
              code: m.code,
              reason: m.reason,
              ...(m.viaParentAutoPick && m.originalCode ? { originalCode: m.originalCode } : {}),
              // commit_removed_existing_code carries the code(s) the extension positively
              // observed getting DE-selected during this commit — needed for the dedicated
              // removal toast (the right provider action is "check the chart", not "add it").
              ...(Array.isArray(m.removedCodes) && m.removedCodes.length > 0 ? { removedCodes: m.removedCodes } : {}),
            });
          });

          // A whole-run extension failure resolves (not throws) as {error, ...} with empty
          // or partial matched/missed — e.g. cdpAttach refused because DevTools is already
          // attached to the EHR tab. Without this, zero codes get recorded, nothing enters
          // the missed toast, and the sync ends with the green success toast while no
          // diagnosis was entered. Any code the extension never reported on is missed.
          if (response?.error) {
            const accounted = new Set([...matched, ...missed].flatMap((m) => [m.code, m.originalCode].filter(Boolean)));
            for (const c of orderedCodes) {
              if (accounted.has(c)) continue;
              recordActionResult(
                action,
                currentIndex,
                currentSection.current,
                makeActionResult({ status: "failed", reason: "ext_error:" + response.error })
              );
              syncMissedCodes.current.push({ type: "ICD-10", code: c, reason: "extension_error" });
            }
          }

          await executeAction(currentIndex + 1);
          break;
        }
        case "pasteSearchPick": {
          // Enablement is controlled by the template author (an internal data-mapper). See pasteAndPickList above.
          if (template.current.system !== "advancedmd") {
            recordActionResult(
              action,
              currentIndex,
              currentSection.current,
              makeActionResult({ status: "failed", reason: "ehr_not_supported" })
            );
            await executeAction(currentIndex + 1);
            break;
          }

          // The annual LOS slot only exists for note types whose losDataset defines one.
          // Where it does not, the Annual select never renders, so the provider cannot
          // have chosen the code sitting there — the parse fallback guessed it, and
          // pasting it adds a second charge-slip line beside the real E/M code. This
          // catches what the slot-family guard structurally cannot: a code in NEITHER
          // family, e.g. the TCM codes (99495/99496) in Clinic New's own enmCode dataset.
          if (
            action.valueSource === "levelOfService" &&
            (action.codeProperty || "code") === "annualCode" &&
            !losDatasetHasAnnualSlot(executionChatRef.current.contextVariables["local:losDataset"])
          ) {
            recordActionResult(
              action,
              currentIndex,
              currentSection.current,
              makeActionResult({ status: "skipped", reason: "no_annual_slot" })
            );
            await executeAction(currentIndex + 1);
            break;
          }

          const source = executionChatRef.current.contextVariables["local:" + action.valueSource];
          const { codes, dropped } = sanitizeCodes(source, action.codeProperty || "code", {
            treatAsSingle: !!action.treatAsSingle,
            notBilledText: notBilledTextApplies(action.valueSource),
            valueSource: action.valueSource,
          });

          const typeLabel = codeListLabel(action.valueSource);

          if (dropped.empty > 0 || dropped.duplicate > 0 || dropped.wrongSlot > 0 || dropped.invalidTokens.length > 0) {
            console.log(
              `LucasAI pasteSearchPick: sanitized ${codes.length} codes (dropped ${dropped.empty} empty, ${dropped.duplicate} duplicate, ${dropped.wrongSlot} wrong-slot, ${dropped.invalidTokens.length} invalid)`
            );
          }
          // Garbled tokens (e.g. the LOS digit-jam "993869921325") must fail
          // VISIBLY: route them into the end-of-sync missed-codes toast rather
          // than silently thinning the list. (syncMissedCodes.current is always
          // initialized by startEHRSync before actions run.)
          for (const tkn of dropped.invalidTokens) {
            syncMissedCodes.current.push({ type: typeLabel, code: tkn, reason: "invalid_code_format" });
          }
          syncMissedCodes.current.push(...newNotBilledMissEntries(syncMissedCodes.current, dropped, typeLabel));

          if (codes.length === 0) {
            recordActionResult(
              action,
              currentIndex,
              currentSection.current,
              makeActionResult({ status: "skipped", reason: "no_codes" })
            );
            await executeAction(currentIndex + 1);
            break;
          }

          let response;
          try {
            response = await lucasIntegrationRequest({
              action: "pasteSearchPick",
              codes,
              searchInput: action.searchInput,
              searchButton: action.searchButton,
              resultList: action.resultList,
              waitAfterTypeMs: action.waitAfterTypeMs || 1500,
              waitAfterSearchMs: action.waitAfterSearchMs || 750,
              waitAfterPickMs: action.waitAfterPickMs || 750,
              waitAfterRefocusMs: action.waitAfterRefocusMs || 300,
            });
          } catch (e) {
            recordActionResult(
              action,
              currentIndex,
              currentSection.current,
              makeActionResult({ status: "failed", reason: "ext_error:" + e.message })
            );
            await executeAction(currentIndex + 1);
            break;
          }

          const matched = response?.matched || [];
          const missed = response?.missed || [];

          matched.forEach(() => {
            recordActionResult(
              action,
              currentIndex,
              currentSection.current,
              makeActionResult({ status: "success", verified: true })
            );
          });
          missed.forEach((m) => {
            recordActionResult(
              action,
              currentIndex,
              currentSection.current,
              makeActionResult({ status: "failed", reason: m.reason })
            );
            if (!syncMissedCodes.current) syncMissedCodes.current = [];
            syncMissedCodes.current.push({ type: typeLabel, code: m.code, reason: m.reason });
          });

          await executeAction(currentIndex + 1);
          break;
        }
        case "setFieldByCode": {
          // AdvancedMD charge slip: find each line by its data-code and write a value into
          // one of its fields (Units, Mod, …). buildFieldsByCode substitutes the code into
          // action.fieldSelectorTemplate; the value is either each item's valueProperty
          // (e.g. cpt2codes.modifier → Units) or a fixed action.fixedValue (e.g. "25" → E/M
          // Mod). Reuses the existing setFieldValue primitive — no Chrome-extension change.
          // A code not on the slip is skipped: not retried, never counted toward the abort budget.
          if (template.current.system !== "advancedmd") {
            recordActionResult(
              action,
              currentIndex,
              currentSection.current,
              makeActionResult({ status: "failed", reason: "ehr_not_supported" })
            );
            await executeAction(currentIndex + 1);
            break;
          }

          // waitBeforeMs is edited as free text, so coerce — a non-numeric value must not
          // become NaN (which setTimeout treats as 0, skipping the wait before the slip renders).
          const waitMs = Number(action.waitBeforeMs);
          if (Number.isFinite(waitMs) && waitMs > 0) {
            await new Promise((resolve) => setTimeout(resolve, waitMs));
          }

          const codeFieldSource = executionChatRef.current.contextVariables["local:" + action.valueSource];
          // buildFieldsByCode's fixed-value mode filters through sanitizeCodes but
          // (deliberately, to keep its array API stable) discards the drop report —
          // re-run the cheap sanitize here so a garbled code (e.g. a digit-jammed
          // LOS enmCode) surfaces in the missed-codes toast like the paste actions,
          // instead of vanishing as a generic no_codes skip.
          if (action.fixedValue != null && String(action.fixedValue).trim() !== "") {
            const { dropped: slipDropped } = sanitizeCodes(codeFieldSource, action.codeProperty || "code", {
              treatAsSingle: !!action.treatAsSingle,
              notBilledText: notBilledTextApplies(action.valueSource),
              valueSource: action.valueSource,
            });
            for (const tkn of slipDropped.invalidTokens) {
              syncMissedCodes.current.push({ type: "Charge Slip", code: tkn, reason: "invalid_code_format" });
            }
            syncMissedCodes.current.push(
              ...newNotBilledMissEntries(syncMissedCodes.current, slipDropped, codeListLabel(action.valueSource))
            );
          }
          const codeFields = buildFieldsByCode(codeFieldSource, {
            codeProperty: action.codeProperty,
            treatAsSingle: action.treatAsSingle,
            notBilledText: notBilledTextApplies(action.valueSource),
            valueSource: action.valueSource,
            valueProperty: action.valueProperty,
            fixedValue: action.fixedValue,
            numericOnly: action.numericOnly,
            fieldSelectorTemplate: action.fieldSelectorTemplate,
          });

          if (codeFields.length === 0) {
            recordActionResult(
              action,
              currentIndex,
              currentSection.current,
              makeActionResult({ status: "skipped", reason: "no_codes" })
            );
            await executeAction(currentIndex + 1);
            break;
          }

          let slipRowsWritten = 0;
          for (const codeField of codeFields) {
            // Extension/tab disconnected mid-loop — stop iterating instead of recording a
            // false "not found" for every remaining code (lucasIntegrationRequest sets this).
            if (cancel.current) break;
            try {
              const result = await lucasIntegrationSetValue(
                { elementType: codeField.elementType, element: codeField.element, useCDP: action.useCDP || false },
                codeField.value,
                currentIndex,
                currentRetry
              );
              if (result.success === "ok") {
                slipRowsWritten++;
                recordActionResult(
                  action,
                  currentIndex,
                  currentSection.current,
                  makeActionResult({
                    status: result.verification?.status || "success",
                    verified: result.verification?.verified || false,
                    mismatchType: result.verification?.mismatchType || null,
                  })
                );
              } else {
                // Line not present on the slip, or the write failed — don't abort the sync, but
                // surface it (like the paste paths) so the provider knows this code's value
                // wasn't applied instead of silently believing the slip synced.
                recordActionResult(
                  action,
                  currentIndex,
                  currentSection.current,
                  makeActionResult({ status: "skipped", reason: "row_not_found:" + codeField.code })
                );
                if (!syncMissedCodes.current) syncMissedCodes.current = [];
                syncMissedCodes.current.push({ type: "Charge Slip", code: codeField.code, reason: "row_not_found" });
              }
            } catch (e) {
              recordActionResult(
                action,
                currentIndex,
                currentSection.current,
                makeActionResult({ status: "skipped", reason: "set_error:" + codeField.code })
              );
              if (!syncMissedCodes.current) syncMissedCodes.current = [];
              syncMissedCodes.current.push({ type: "Charge Slip", code: codeField.code, reason: "set_error" });
            }
          }

          // Writing zero fields has two causes that look identical from here and mean
          // opposite things:
          //   • the charge slip never rendered — the "charge slip not appearing" complaint
          //     (green sync toast, empty slip). A real, visible sync error.
          //   • the slip is open, but none of THIS action's codes are on it, or those
          //     lines don't carry this field. Normal: a template runs a Units step and a
          //     Modifier-25 step over different code lists, so one matching nothing while
          //     the other writes fine is an everyday outcome, not a failed sync. The
          //     per-code "codes not found in EHR" warning already covers it.
          // Ask the page which one it is instead of assuming the worst — the previous
          // assumption turned a working sync red on every charge slip. The probe is the
          // code-agnostic prefix of this action's own selector, so it is proven against
          // this EHR rather than guessed.
          if (!cancel.current && slipRowsWritten === 0) {
            const slipRowSelector = deriveSlipRowSelector(action.fieldSelectorTemplate);
            const slipRendered = slipRowSelector
              ? await pollForElement(slipRowSelector, SLIP_PRESENCE_TIMEOUT_MS, "path")
              : false;
            // Re-check cancel: pollForElement also returns false when the sync was
            // cancelled or the tab went away mid-probe, which is not a missing slip.
            if (!slipRendered && !cancel.current) {
              lucasIntegrationSyncError(
                currentIndex,
                action,
                "No charge-slip line matched any code — the charge slip may not have opened"
              );
            } else if (slipRendered) {
              console.warn(
                "EHR Sync: action " +
                  currentIndex +
                  " (setFieldByCode) wrote no fields, but the charge slip is open — codes not on the slip"
              );
            }
          }

          await executeAction(currentIndex + 1);
          break;
        }
        case "resolveInsuranceProvider": {
          // Read the patient's carrier, plan, and DOB from the AdvancedMD page, evaluate the
          // template's rule table, and store the answer in context variables. This step never
          // writes to the EHR — a downstream `set` step places {{note:mappedProvider}}.
          //
          // Any uncertainty (unknown carrier, unreadable plan, two valid doctors, a refusal
          // row) resolves to no provider, and the downstream `set` leaves the field alone.
          // Misrouting a claim to the wrong Tax ID is worse than an empty field.
          //
          // PHI: log the rule id and status only — never the carrier, plan, or DOB.
          if (template.current.system !== "advancedmd") {
            recordActionResult(
              action,
              currentIndex,
              currentSection.current,
              makeActionResult({ status: "failed", reason: "ehr_not_supported" })
            );
            await executeAction(currentIndex + 1);
            break;
          }

          // waitBeforeMs is edited as free text — coerce so a non-numeric value doesn't
          // become NaN (which setTimeout treats as 0, skipping the wait before the render).
          const insWaitMs = Number(action.waitBeforeMs);
          if (Number.isFinite(insWaitMs) && insWaitMs > 0) {
            await new Promise((resolve) => setTimeout(resolve, insWaitMs));
          }

          // Try each configured source in order until one yields a non-empty value. Sources
          // are template data so the DOM paths can be corrected without a deploy.
          // Coerce a getFieldValue item to matchable text. Only strings/numbers are
          // trusted: objects reaching here are serialization artifacts (e.g. a
          // cross-realm DOM node flattened to `{}` by the extension bridge), and
          // String()-ing one poisons the rule table with "[object Object]".
          // Residual markup (innerHTML reads) is stripped and whitespace collapsed.
          const asFieldText = (v) => {
            if (typeof v !== "string" && typeof v !== "number") return "";
            return String(v)
              .replace(/<[^>]*>/g, " ")
              .replace(/\s+/g, " ")
              .trim();
          };

          const readFieldText = async (source, viaTextContent) => {
            const field = { elementType: source.elementType, element: source.element };
            if (viaTextContent) field.property = "textContent";
            const response = await lucasIntegrationRequest({ action: "getFieldValue", fields: [field] });
            const value = Array.isArray(response) ? response.find((v) => asFieldText(v) !== "") : null;
            return asFieldText(value);
          };

          const readFirstNonEmpty = async (sources) => {
            if (!Array.isArray(sources)) return "";
            for (const source of sources) {
              if (cancel.current) return "";
              try {
                // Ask for textContent explicitly first: the extension applies property
                // reads BEFORE its realm-sensitive instanceof chain, so cross-realm
                // iframe nodes come back as plain text (no "[object Object]", no
                // innerHTML/tooltip markup) even on extension builds deployed before
                // the getFieldValue realm fix. Fall back to a plain read for sources
                // that resolve to INPUT/SELECT-style elements, whose value doesn't
                // live in textContent.
                const text = (await readFieldText(source, true)) || (await readFieldText(source, false));
                if (text !== "") return text;
              } catch (e) {
                // Path didn't resolve on this screen — try the next source.
              }
            }
            return "";
          };

          // Diagnostic probe (PHI-safe: counts only, never values). Zero/missing counts
          // mean the template reached the sync without its insurance config — a
          // save/round-trip problem, not a DOM one.
          const cfgCount = (v) => (Array.isArray(v) ? v.length : "missing");
          console.log(
            "EHR Sync: resolver config — carrierSources:" +
              cfgCount(action.carrierSources) +
              " planSources:" +
              cfgCount(action.planSources) +
              " dobSources:" +
              cfgCount(action.dobSources) +
              " rules:" +
              cfgCount(action.rules)
          );

          // Condition-based wait: poll until the carrier field actually yields text
          // instead of trusting a fixed pre-wait tuned to one network speed — a fixed
          // wait is simultaneously too short on throttled connections (demographics
          // never loads in time) and too long on fast ones. Fast connections exit on
          // the first read (~one round-trip); slow ones get the full ceiling before
          // the resolver concludes "carrier absent". Each poll is the same
          // getFieldValue request the shipped extension already serves, so this needs
          // no extension release. waitForContentMs is template data edited as free
          // text — coerce, and fall back to 12s when missing/invalid. Only poll when
          // carrierSources is actually configured: polling a missing config would
          // burn the whole ceiling to learn nothing.
          const contentCeilingRaw = Number(action.waitForContentMs);
          const contentCeilingMs = Number.isFinite(contentCeilingRaw) && contentCeilingRaw > 0 ? contentCeilingRaw : 12000;
          const contentPollMs = 500;
          const canPollCarrier = Array.isArray(action.carrierSources) && action.carrierSources.length > 0;
          const contentStart = Date.now();
          let contentPolls = 1;
          let carrierText = await readFirstNonEmpty(action.carrierSources);
          while (
            canPollCarrier &&
            carrierText === "" &&
            !cancel.current &&
            Date.now() - contentStart + contentPollMs <= contentCeilingMs
          ) {
            await new Promise((resolve) => setTimeout(resolve, contentPollMs));
            carrierText = await readFirstNonEmpty(action.carrierSources);
            contentPolls += 1;
          }
          // PHI-safe: presence, poll count, and elapsed only — never the value.
          console.log(
            "EHR Sync: resolver carrier read — " +
              (carrierText ? "present" : "EMPTY") +
              " after " +
              contentPolls +
              " poll(s), " +
              (Date.now() - contentStart) +
              "ms (ceiling " +
              contentCeilingMs +
              "ms)"
          );
          const planText = await readFirstNonEmpty(action.planSources);

          // Prefer the DOB already on the note's patient record — authoritative, and no
          // DOM dependency. parseAgeFromWidget accepts both record formats (MM/DD/YYYY
          // and ISO). The template's dobSources stay as a fallback for chats whose
          // record is missing a DOB.
          const recordDob = executionChatRef.current?.user?.dob;
          const dobText =
            recordDob != null && String(recordDob).trim() !== "" ? String(recordDob) : await readFirstNonEmpty(action.dobSources);

          // Deep-debug gate: automatic on the dev server; production builds still require
          // the explicit per-browser localStorage opt-in (the raw reads are PHI):
          //   localStorage.setItem("ehrSyncDebug", "1")   — remove with removeItem.
          const ehrSyncDebug =
            process.env.NODE_ENV === "development" ||
            (typeof localStorage !== "undefined" && localStorage.getItem("ehrSyncDebug") === "1");

          // Second probe: which inputs actually came back from the page (presence only —
          // logging the values themselves would be PHI).
          console.log(
            "EHR Sync: resolver inputs — carrier " +
              (carrierText ? "present" : "EMPTY") +
              ", plan " +
              (planText ? "present" : "EMPTY") +
              ", dob " +
              (dobText ? "present" : "EMPTY") +
              " (source: " +
              (executionChatRef.current?.user?.dob ? "record" : "dom") +
              ") debug:" +
              (ehrSyncDebug ? "on" : "off")
          );

          if (ehrSyncDebug) {
            console.log(
              "EHR Sync [debug]: carrier=" +
                JSON.stringify(carrierText) +
                " plan=" +
                JSON.stringify(planText) +
                " dob=" +
                JSON.stringify(dobText)
            );
          }

          const resolution = resolveProvider(
            { carrier: carrierText, plan: planText, age: parseAgeFromWidget(dobText) },
            action.rules
          );

          const targetVariable = action.targetVariable || "mappedProvider";
          const contextVariables = executionChatRef.current.contextVariables;
          contextVariables["local:" + targetVariable] = resolution.provider || "";
          contextVariables["local:mappedTaxId"] = resolution.taxId || "";
          contextVariables["local:mappedBillingEntity"] = resolution.billingEntity || "";
          contextVariables["local:mappedRuleId"] = resolution.ruleId || "";
          contextVariables["local:mappedStatus"] = resolution.status;

          // On ties the resolver has no single ruleId but does know the candidates —
          // print them so an "ambiguous" outcome is self-explanatory in the console.
          const ruleLabel =
            resolution.ruleId ||
            (Array.isArray(resolution.candidateRuleIds) && resolution.candidateRuleIds.length > 0
              ? "tie: " + resolution.candidateRuleIds.join(" | ")
              : "none");
          console.log("EHR Sync: insurance mapping " + resolution.status + " (rule: " + ruleLabel + ")");

          if (resolution.status === "matched") {
            recordActionResult(action, currentIndex, currentSection.current, makeActionResult({ status: "success" }));
          } else {
            // Every non-matched outcome is `skipped`, never `failed`: a patient whose carrier
            // isn't on the sheet must not consume the 3-error abort budget or kill the sync.
            recordActionResult(
              action,
              currentIndex,
              currentSection.current,
              makeActionResult({ status: "skipped", reason: resolution.status })
            );
          }

          await executeAction(currentIndex + 1);
          break;
        }
        case "click": {
          // DEPRECATED, USE EVENT
          try {
            const clickElement = { elementType: action.elementType, element: action.element };
            if (action.fingerprint) clickElement.fingerprint = action.fingerprint;
            const response = await lucasIntegrationRequest({
              action: "click",
              element: clickElement,
            });

            // Capture fingerprint from resolver for progressive enrichment
            if (response && response._fp) {
              action._capturedFingerprint = response._fp;
            }

            if (response === null) {
              console.log("EHR Sync: element not found, retrying (" + (currentRetry + 1) + "/" + maxRetries + ")");
              setTimeout(
                () => {
                  executeAction(currentIndex, currentRetry + 1, "Element not found");
                },
                500 + 100 * currentRetry
              );
            } else {
              const resolverStatus =
                (action._resolverConfidence ?? 1) < 0.85 || action._resolverStrategy === "fingerprint" ? "uncertain" : "success";
              recordActionResult(
                action,
                currentIndex,
                currentSection.current,
                makeActionResult({
                  strategy: action._resolverStrategy || "none",
                  confidence: action._resolverConfidence || 0,
                  status: resolverStatus,
                })
              );
              await executeAction(currentIndex + 1);
            }
          } catch (e) {
            console.error("Error in click action", e);
            await executeAction(currentIndex, currentRetry + 1, "Element not found");
          }
          break;
        }

        case "if": {
          let expression = action.value;
          expression = lucasIntegrationGetContent(expression, currentIndex, action);
          try {
            if (expression.indexOf("return ") === -1)
              // BACKWARD COMPATIBLE
              expression = "return " + expression;

            // eslint-disable-next-line no-new-func
            const result = new Function("note", expression)(executionChatRef.current);
            recordActionResult(action, currentIndex, currentSection.current, makeActionResult());
            await executeAction(currentIndex + (result ? 1 : 2)); // SKIP NEXT IF FALSE
          } catch (e) {
            console.log("EHR Sync: Error on evaluating if expression at index", currentIndex, e.message);
            lucasIntegrationSyncError(currentIndex, action, "error on evaluating if expression at index " + currentIndex);
          }
          break;
        }

        case "merge":
          if (template.current.system === "eclinicalworks") {
            try {
              await lucasIntegrationUpdateECWTable(
                currentIndex,
                action,
                action.element,
                action.newElement,
                action.value,
                action.prompt
              );
              recordActionResult(action, currentIndex, currentSection.current, makeActionResult({ verified: true }));
              await executeAction(currentIndex + 1);
            } catch (e) {
              console.error("Error in merge action", e);
              lucasIntegrationSyncError(currentIndex, action, "Error in merge action: " + e.message);
            }
          } else {
            lucasIntegrationSyncError(currentIndex, action, "Merge action not compatible with EHR " + template.current.system);
          }
          break;

        case "wait":
          setTimeout(() => {
            executeAction(currentIndex + 1);
          }, action.value);
          break;

        case "waitFor": {
          // Condition-based replacement for fixed `wait`: poll the given DOM sources
          // until one yields non-empty text, then continue immediately. A fixed wait
          // charges every network the same toll — too short on throttled connections,
          // too long on fast ones. On timeout the sync continues anyway: downstream
          // steps carry their own guards, and a visible "uncertain" beats a stall.
          // NOTE: templates using this action require a webapp build that includes it —
          // older builds hit the default case on unknown types.
          const asWaitText = (v) => {
            if (typeof v !== "string" && typeof v !== "number") return "";
            return String(v)
              .replace(/<[^>]*>/g, " ")
              .replace(/\s+/g, " ")
              .trim();
          };
          const readWaitSource = async (source, viaTextContent) => {
            const field = { elementType: source.elementType, element: source.element };
            if (viaTextContent) field.property = "textContent";
            const response = await lucasIntegrationRequest({ action: "getFieldValue", fields: [field] });
            const value = Array.isArray(response) ? response.find((v) => asWaitText(v) !== "") : null;
            return asWaitText(value);
          };
          // Accept either a sources array or a single element/elementType pair, and
          // drop entries with no element — polling a blank path would burn the whole
          // ceiling to learn nothing. Template data is edited as free text — coerce.
          const waitSources = (
            Array.isArray(action.sources) && action.sources.length > 0
              ? action.sources
              : [{ elementType: action.elementType, element: action.element }]
          ).filter((s) => s && typeof s.element === "string" && s.element !== "");
          if (waitSources.length === 0) {
            recordActionResult(
              action,
              currentIndex,
              currentSection.current,
              makeActionResult({ status: "uncertain", reason: "waitfor_no_sources" })
            );
            await executeAction(currentIndex + 1);
            break;
          }
          const waitCeilingRaw = Number(action.timeoutMs);
          const waitCeilingMs = Number.isFinite(waitCeilingRaw) && waitCeilingRaw > 0 ? waitCeilingRaw : 15000;
          const waitPollRaw = Number(action.pollMs);
          const waitPollMs = Number.isFinite(waitPollRaw) && waitPollRaw > 0 ? waitPollRaw : 500;
          const waitStart = Date.now();
          let waitMatched = false;
          while (!cancel.current) {
            for (const source of waitSources) {
              try {
                const text = (await readWaitSource(source, true)) || (await readWaitSource(source, false));
                if (text !== "") {
                  waitMatched = true;
                  break;
                }
              } catch (e) {
                // Path didn't resolve on this screen — try the next source.
              }
            }
            if (waitMatched || Date.now() - waitStart + waitPollMs > waitCeilingMs) break;
            await new Promise((resolve) => setTimeout(resolve, waitPollMs));
          }
          // floorMs: minimum total elapsed even on an instant match. In Angular UIs an
          // element can exist in the DOM before its handlers attach — "present" is not
          // "interactive". Without a floor, a pre-rendered target collapses the wait to
          // ~0ms and the next click can land on a dead element.
          const waitFloorRaw = Number(action.floorMs);
          const waitFloorMs = Number.isFinite(waitFloorRaw) && waitFloorRaw > 0 ? waitFloorRaw : 0;
          const waitRemainderMs = waitFloorMs - (Date.now() - waitStart);
          if (waitMatched && waitRemainderMs > 0) {
            await new Promise((resolve) => setTimeout(resolve, waitRemainderMs));
          }
          // PHI-safe: outcome and elapsed only — never the value.
          console.log("EHR Sync: waitFor " + (waitMatched ? "matched" : "TIMEOUT") + " after " + (Date.now() - waitStart) + "ms");
          recordActionResult(
            action,
            currentIndex,
            currentSection.current,
            makeActionResult(waitMatched ? { status: "success" } : { status: "uncertain", reason: "wait_timeout" })
          );
          await executeAction(currentIndex + 1);
          break;
        }

        case "dynamic": {
          const variableName = action.variableName;
          if (variableName === null || variableName === "") {
            globalNotify("No dynamic variable", "defined in " + template.current.system, "danger");
            await executeAction(currentIndex + 1);
          }

          const variableValue = executionChatRef.current.contextVariables["local:" + variableName];

          let syncOk = [];
          let syncError = [];
          let syncNotFound = [];

          let configuration = JSON.parse(action.value);
          for (let elementId in variableValue) {
            const elementValue = variableValue[elementId];
            if (elementValue != null) {
              let elementFound = false;
              for (let entry of configuration) {
                if (entry.id === elementValue.id) {
                  elementFound = true;

                  let value = elementValue.value;

                  if (elementValue.constraints != null) {
                    elementValue.constraints.split(",").forEach(function (constraint) {
                      if (constraint.startsWith("maxlength=")) {
                        const maxLength = parseInt(constraint.substring("maxlength=".length));
                        if (value != null && value.length > maxLength) value = value.substring(0, maxLength);
                      }
                    });
                  }

                  if (action.convert != null)
                    // CONVERT THE VALUE WITH A CUSTOM FUNCTION
                    value = action.convert(value);

                  if (value === null || value === "" || value === "null" || value === "undefined" || value === "[EMPTY]")
                    continue;

                  let setFields = [];

                  if (entry.type === "checkbox" && entry.options != null && elementValue.value != null) {
                    // SELECT EACH OPTIONS
                    for (let i = 0; i < entry.options.length; ++i) {
                      const opt = entry.options[i];
                      setFields.push({ elementType: "path", element: opt.path, value: elementValue.value.includes(opt.id) });
                    }
                  } else {
                    if (entry.type === "radio" || entry.type === "checkbox")
                      // OVERRIDE TO TRUE (CHECKED/SELECTED)
                      value = true;

                    setFields.push({ elementType: "path", element: entry.path, value: value });
                  }

                  await Promise.all(
                    setFields.map(async (setField) => {
                      const result = await lucasIntegrationSetValue(setField, setField.value, currentIndex, currentRetry);
                      if (result.success === "ok") {
                        syncOk.push(elementValue.id);
                      } else {
                        syncError.push(elementValue.id);
                      }
                    })
                  );
                  break;
                }
              }

              if (!elementFound) syncNotFound.push(elementValue.id);
            }
          }

          console.log(
            "EHR Sync: dynamic component, synchronized=" + syncOk + " error=" + syncError + " notFound=" + syncNotFound
          );
          recordActionResult(action, currentIndex, currentSection.current, makeActionResult());
          await executeAction(currentIndex + 1);
          break;
        }

        case "code": {
          let expression = action.value;
          if (expression === null || expression === "") {
            console.log("EHR Sync: empty code expression, skipping");
          } else {
            try {
              // eslint-disable-next-line
              const result = new Function("note", expression)(executionChatRef.current);
              console.log("EHR Sync: Executed code at index " + currentIndex);
              recordActionResult(action, currentIndex, currentSection.current, makeActionResult());
              await executeAction(currentIndex + 1);
            } catch (e) {
              console.log("EHR Sync: Error on evaluating code at index", currentIndex, e.message);
              lucasIntegrationSyncError(currentIndex, action, "error on evaluating code at index " + currentIndex);
            }
          }
          break;
        }
        default:
          // Forward-compat guard: a template saved by a newer webapp may carry an
          // action type this build doesn't implement. The old bare `break` advanced
          // nothing, so one unknown action silently froze the entire sync. Skip it,
          // record it, and keep going — a visible "skipped" beats an invisible hang.
          console.log("EHR Sync: unknown action type at index " + currentIndex + " — skipped (" + action.type + ")");
          recordActionResult(
            action,
            currentIndex,
            currentSection.current,
            makeActionResult({ status: "skipped", reason: "unknown_action_type" })
          );
          await executeAction(currentIndex + 1);
          break;
      }
    } catch (e) {
      console.error("Unexpected error during action execution", e);
      executeAction(currentIndex, currentRetry + 1, "Unexpected error: " + e.message);
    }
  };

  // Utils

  let globalNotify = (title, message, type, duration) => {
    console.log("Notification: " + title + " - " + message);
  };

  const lucasIntegrationSendSyncError = () => {
    console.log("EHR Sync: sending sync error report (" + errors.length + " errors)");
  };

  const lucasIntegrationSyncError = (index, action, reason) => {
    const resolvedIndex = action?.index ?? index;
    // DUPLICATE AVOIDANCE
    if (errors.length > 0 && errors[errors.length - 1].index === resolvedIndex) return;

    errors.push({ section: currentSection.current, index: resolvedIndex, actionType: action?.type, reason: reason });
    setErrors([...errors]);
    console.warn("EHR Sync: action " + resolvedIndex + " (" + action?.type + ") skipped - " + errors.length + " total errors");
    publish("ehrSyncError", { actionType: action?.type, index: resolvedIndex, errorCount: errors.length });
  };

  function lucasIntegrationGetContent(content, currentIndex, action) {
    if (content === null || content === "") return "";

    while (true) {
      let begin = content.indexOf("{{");
      if (begin > -1) {
        let end = content.indexOf("}}", begin);
        if (end === -1) {
          console.error("Error on formatting field content: }} not found");
          lucasIntegrationSyncError(currentIndex, action, "error on formatting field content: }} not found");
          break;
        } else {
          let part = "";
          let varName = content.substring(begin + 2, end);
          if (varName.startsWith("note:")) varName = "local:" + varName.substring("note:".length);

          let sectionTitle = null;
          const sepPos = varName.indexOf("|");
          if (sepPos > -1) {
            sectionTitle = varName.substring(sepPos + 1);
            varName = varName.substring(0, sepPos);
          }

          part = executionChatRef.current?.contextVariables?.[varName];
          // Plan of Care is stored as an HTML string or a {key, value} array
          // depending on whether the chart UI has touched it — sync the array
          // shape either way so the outline doesn't vanish after a re-record.
          part = normalizePlanOfCareForSync(varName, part);
          // Scored code lists (billableCodesICD10 et al.) degrade to bare key
          // names under lucasFormatVariable's one-level nesting cap — render
          // them as "<code> - <description>" bullets instead.
          part = formatCodedListHtml(part, varName) ?? lucasFormatVariable(part, null, 1, null, 1);

          if (sectionTitle != null && part != null && part !== "") part = sectionTitle + part;

          if (part === null) part = "";

          content = content.substring(0, begin) + part + content.substring(end + 2);
        }
      } else {
        begin = content.indexOf("[");
        if (begin > -1 && content[begin + 1] !== "[") begin = -1;

        if (begin > -1) {
          let end = content.indexOf("]]", begin);
          if (end === -1) {
            console.error("Error on formatting field content: ]] not found");
            lucasIntegrationSyncError(currentIndex, action, "error on formatting field content: ]] not found");
            break;
          } else {
            let code = removeNewLines(content.substring(begin + 2, end).trim());

            let part = null;
            try {
              if (code.indexOf("return ") === -1)
                // BACKWARD COMPATIBLE
                code = "return " + code;

              // eslint-disable-next-line no-new-func
              part = new Function("note", code)(executionChatRef.current);
              // Prevent [object Object] — format complex values the same way {{...}} does
              if (part !== null && part !== undefined && typeof part === "object") {
                part = formatCodedListHtml(part) ?? lucasFormatVariable(part, null, 1, null, 1);
              }
            } catch (e) {
              // MANY EXPRESSIONS CAN FAIL BECAUSE OF NULL, JUST LOG THEM
              console.log("EHR Sync: Error on evaluating content to push to EHR", e);
              // lucasSendEvent("UNMANAGED_ERROR", "WARNING", "EHR: error on evaluating content in section '"+lucasIntegrationLastSection+"', note "+globalDoctorLastChat.id+", system '"+lucasIntegrationEHR+"', noteType '"+ lucasIntegrationEncounterType +"': " + e);
            }

            if (part === null) part = "";

            content = content.substring(0, begin) + part + content.substring(end + 2);
          }
        } else break;
      }
    }

    return content;
  }

  function lucasFormatVariable(
    content,
    output,
    currentNestedLevel,
    sectionIdx,
    maxNestedLevels,
    formatFunction,
    afterRenderingCallbacks
  ) {
    if (output == null) output = "";

    if (formatFunction != null) return window[formatFunction](sectionIdx, content, afterRenderingCallbacks);

    if (content == null) return output;

    if (currentNestedLevel == null) currentNestedLevel = 1;

    if (typeof content === "string" || content instanceof String)
      // PARSE THE STRING INTO JSON
      try {
        const json = JSON.parse(content);
        content = json;
      } catch (e) {}

    if (Array.isArray(content)) {
      output += "<ul>\n";
      for (let i = 0; i < content.length; ++i) {
        output += "<li>";
        if (content[i]?.key !== undefined && content[i]?.value !== undefined) {
          if (!content[i].value && content[i].key) {
            output += content[i].key;
          } else {
            output += "<strong>" + content[i].key + "</strong>: ";
            output = lucasFormatVariable(content[i].value, output, currentNestedLevel + 1, sectionIdx, maxNestedLevels);
          }
        } else {
          output = lucasFormatVariable(content[i], output, currentNestedLevel + 1, sectionIdx, maxNestedLevels);
        }
        output += "</li>\n";
      }
      output += "</ul>";
    } else if (typeof content === "object") {
      output += "<ul>\n";
      for (let key in content) {
        if (maxNestedLevels == null || currentNestedLevel < maxNestedLevels) {
          output += "<li><strong>" + key + "</strong>: ";
          output = lucasFormatVariable(content[key], output, currentNestedLevel + 1, sectionIdx, maxNestedLevels);
        } else output += "<li>" + key;
        output += "</li>";
      }
      output += "</ul>";
    } else output += content;

    return lucasMinifyHTML(output);
  }

  function lucasMinifyHTML(html) {
    return html
      .replace(/<!--.*?-->/g, "") // Remove HTML comments
      .replace(/\s+/g, " ") // Replace multiple spaces with a single space
      .replace(/>\s</g, "><") // Remove spaces between tags
      .trim(); // Remove leading/trailing spaces
  }

  function removeNewLines(text) {
    while (text.startsWith("\n")) text = text.substring(2);
    while (text.endsWith("\n")) text = text.substring(0, text.length - 2);
    return text;
  }

  function doctorElemToText(text, elements, indent, newLineAfterParagraphs) {
    let value = elements.innerText;
    return value;
  }

  function lucasConvertToBasicChars(inputString) {
    if (inputString === null) return null;

    // A map of special characters to their basic equivalents.
    const charMap = {
      "—": "-", // Em dash
      "–": "-", // En dash
      "‘": "'", // Left single quote
      "’": "'", // Right single quote
      "“": '"', // Left double quote
      "”": '"', // Right double quote
      "`": "'", // Backtick
      "…": "...", // Ellipsis
      "•": "*", // Bullet
    };

    // 1. Replace specific symbols and smart punctuation.
    // Creates a regex like /—|–|‘|’|“|”|`|…|•/g
    const regex = new RegExp(Object.keys(charMap).join("|"), "g");
    let sanitizedString = inputString.replace(regex, (match) => charMap[match]);

    // 2. Decompose accented characters and remove the accent marks.
    // 'é' becomes 'e' + '´', then the '´' is removed.
    return sanitizedString.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  }

  // --- Phase 2: Post-Injection Verification ---

  function getActionKey(action, sectionName, actionIndex) {
    // Stable identity for cross-sync streak detection
    // Format: "actionType:sectionName:index" — NO selectors stored (spec constraint)
    const type = action?.type || "unknown";
    const section = sectionName || "global";
    return type + ":" + section + ":" + actionIndex;
  }

  function makeActionResult(overrides) {
    return {
      strategy: "notApplicable",
      confidence: 1,
      timeMs: 0,
      status: "success",
      verified: false,
      mismatchType: null,
      ...overrides,
    };
  }

  // --- Phase 3: Safeguards ---
  const CONFIDENCE_SKIP_THRESHOLD = 0.7;
  const MAX_ERRORS_BEFORE_ABORT = 3;

  function shouldSkipByConfidence(action) {
    // Returns true if confidence is too low to inject safely
    const confidence = action._resolverConfidence;
    if (confidence === undefined || confidence === null) return false; // no resolver data = legacy path, allow
    return confidence < CONFIDENCE_SKIP_THRESHOLD;
  }

  function shouldSkipByFieldType(action, resolverResult) {
    // Returns true if the resolved element type doesn't match the expected action
    if (!resolverResult || !resolverResult.elementTag) return false; // no metadata = legacy, allow

    const tag = resolverResult.elementTag;
    const inputType = resolverResult.elementInputType || "";

    if (action.type === "set") {
      // Text values should not go into checkboxes or radios
      if (tag === "input" && (inputType === "checkbox" || inputType === "radio")) {
        return true;
      }
    }

    if (action.type === "event" || action.type === "click") {
      // Clicks should not target text inputs
      if (tag === "input" && (inputType === "text" || inputType === "search" || inputType === "email" || inputType === "tel")) {
        return true;
      }
    }

    return false;
  }

  function findSectionIndex(sectionTitle) {
    // Find the template index of a section by its title
    if (!template.current || !template.current.actions) return -1;
    for (let i = 0; i < template.current.actions.length; i++) {
      const a = template.current.actions[i];
      if (a.type === "section" && a.value === sectionTitle) return i;
    }
    return -1;
  }

  function queueFullSectionContent(sectionTitle, actionIndex, reason, opts) {
    // Queue the FULL section content for Smart Paste (spec: always full section, not individual fields)
    const sectionIdx = findSectionIndex(sectionTitle);
    if (sectionIdx === -1) return;
    const content = computeSectionContent(sectionIdx);
    if (content) {
      queueFailedSection(sectionTitle, content, actionIndex, reason, opts);
    }
  }

  function queueFailedSection(sectionTitle, content, actionIndex, reason, opts) {
    // content can be a string or { html, text } object from computeSectionContent
    // opts: { isDropdown, elementType, element, panelHint }
    const textContent = typeof content === "string" ? content : content?.text;
    const htmlContent = typeof content === "string" ? content : content?.html;

    // Don't queue if no content (template errors, click/event actions)
    if (!textContent || textContent.trim() === "") return;

    // Group by section — check if this section already has an entry
    const existing = failedSections.current.find((s) => s.sectionTitle === sectionTitle);
    if (existing) {
      return;
    }

    const o = opts || {};
    failedSections.current.push({
      sectionTitle: sectionTitle || "Field at position " + actionIndex,
      content: textContent,
      htmlContent: htmlContent || textContent,
      actionIndex: actionIndex,
      reason: reason,
      isDropdown: !!o.isDropdown,
      elementType: o.elementType || null,
      element: o.element || null,
      panelHint: o.panelHint || null,
    });
  }

  function computeSectionContent(sectionIndex) {
    // Compute the full content for a section using the same formatting as the real sync
    // Returns { html, text } so clipboard can write both formats (preserves bullets/formatting)
    const actions = template.current.actions;
    let htmlParts = [];
    let textParts = [];
    let started = false;
    const isECW = template.current.system === "eclinicalworks";

    for (let i = sectionIndex; i < actions.length; i++) {
      const a = actions[i];
      if (a.type === "section") {
        if (started) break;
        started = true;
        continue;
      }
      if (!started) continue;

      try {
        if ((a.type === "set" || a.type === "merge") && (a.value || a.useCopyAll)) {
          // Use cached resolved value if available (fix #4: physicalExams context may be empty post-sync)
          let rawValue = a._resolvedValue;
          if (!rawValue) {
            const copyAllResolved = resolveCopyAllValue(a);
            rawValue = copyAllResolved !== null ? copyAllResolved : lucasIntegrationGetContent(a.value, i, a);
          }
          if (!rawValue || rawValue.trim() === "") continue;

          // HTML version (for rich paste — preserves bullets, formatting)
          htmlParts.push(rawValue);

          // Plain text version — preserve bullet structure for textarea/input paste
          let textValue = rawValue
            .replace(/<ul[^>]*>/gi, "")
            .replace(/<\/ul>/gi, "")
            .replace(/<ol[^>]*>/gi, "")
            .replace(/<\/ol>/gi, "")
            .replace(/<li[^>]*>/gi, "- ")
            .replace(/<\/li>/gi, "\n")
            .replace(/<br\s*\/?>/gi, "\n")
            .replace(/<p[^>]*>/gi, "\n")
            .replace(/<\/p>/gi, "\n")
            .replace(/<strong[^>]*>/gi, "")
            .replace(/<\/strong>/gi, "")
            .replace(/<em[^>]*>/gi, "")
            .replace(/<\/em>/gi, "")
            .replace(/<[^>]*>/g, "") // catch-all for any remaining tags (including malformed)
            .replace(/<\/[a-z]+/gi, "") // catch malformed closing tags missing >
            .replace(/&amp;/g, "&")
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&nbsp;/g, " ")
            .replace(/\n{3,}/g, "\n\n")
            .trim();
          if (isECW) textValue = lucasConvertToBasicChars(textValue);
          if (textValue !== "") textParts.push(textValue);
        }
      } catch (e) {
        // Template content error — skip this field
      }
    }

    if (textParts.length === 0 && htmlParts.length === 0) return null;

    // Convert to format compatible with wysihtml and other EHR editors
    // - Replace <ul><li> with "- " bullets (wysihtml strips ul/li tags)
    // - Keep <strong>/<b> for bold (widely accepted by rich text editors)
    let htmlOutput = htmlParts.join("<br><br>");
    htmlOutput = htmlOutput
      .replace(/<ul[^>]*>/gi, "")
      .replace(/<\/ul>/gi, "")
      .replace(/<ol[^>]*>/gi, "")
      .replace(/<\/ol>/gi, "")
      .replace(/<li[^>]*>/gi, "- ")
      .replace(/<\/li>/gi, "<br>")
      .replace(/<strong[^>]*>/gi, "<b>")
      .replace(/<\/strong>/gi, "</b>");

    return {
      html: htmlOutput,
      text: textParts.join("\n\n"),
    };
  }

  function queueAllRemainingSections(reason) {
    // Queue ALL sections for Smart Paste — used on infrastructure failures
    if (!template.current || !template.current.actions) return;
    const actions = template.current.actions;
    for (let i = 0; i < actions.length; i++) {
      const a = actions[i];
      if (a.type === "section") {
        const content = computeSectionContent(i);
        if (content) {
          queueFailedSection(a.value, content, i, reason);
        }
      }
    }
    if (failedSections.current.length > 0) {
      setSmartPasteQueue([...failedSections.current]);
    }
  }

  const injectSectionField = async (field, htmlContent) => {
    // Apply mode: inject content into a rich text field using the same path as sync
    const fieldDef = { elementType: field.elementType, element: field.element, value: htmlContent };
    const response = await lucasIntegrationRequest({
      action: "setFieldValue",
      field: fieldDef,
    });
    if (!response || response.error) {
      throw new Error(response?.errorMsg || "Apply failed");
    }
    return response;
  };

  const handleSmartPasteComplete = (result) => {
    // Update the last sync report with recovery data
    // Note: finalizeSyncReport has already saved syncHistory by this point, so no race condition
    if (template.current?.syncHistory?.length > 0) {
      const lastReport = template.current.syncHistory[template.current.syncHistory.length - 1];
      lastReport.recoveryCompleted = true;
      lastReport.sectionsRecovered = result.sectionsRecovered || 0;
      lastReport.sectionsCancelled = result.sectionsCancelled || 0;
      api
        .saveEhrTemplate(template.current.id, { syncHistory: template.current.syncHistory })
        .catch(() => console.error("EHR Sync: failed to save recovery tracking"));
    }
    setSmartPasteQueue(null);
    failedSections.current = [];
  };

  const handleSmartPasteCancel = (result) => {
    if (template.current?.syncHistory?.length > 0) {
      const lastReport = template.current.syncHistory[template.current.syncHistory.length - 1];
      lastReport.recoveryCompleted = false;
      lastReport.sectionsRecovered = result.sectionsRecovered || 0;
      lastReport.sectionsCancelled = result.sectionsCancelled || 0;
      api
        .saveEhrTemplate(template.current.id, { syncHistory: template.current.syncHistory })
        .catch(() => console.error("EHR Sync: failed to save recovery tracking"));
    }
    setSmartPasteQueue(null);
    failedSections.current = [];
  };

  function recordActionResult(action, index, sectionName, result) {
    // Record per-action result for the sync report
    syncReportActions.current.push({
      index: index,
      actionType: action?.type || "unknown",
      key: getActionKey(action, sectionName, index),
      strategy: result.strategy,
      confidence: result.confidence,
      timeMs: result.timeMs,
      status: result.status,
      verified: result.verified,
      mismatchType: result.mismatchType,
    });
  }

  function buildSyncReport(completionStatus) {
    const actions = syncReportActions.current;
    const counts = { success: 0, failed: 0, mismatch: 0, skipped: 0, uncertain: 0 };
    const strategies = { exact: 0, xpath: 0, fingerprint: 0, none: 0, notApplicable: 0 };
    for (let i = 0; i < actions.length; i++) {
      const a = actions[i];
      if (counts[a.status] !== undefined) counts[a.status]++;
      if (strategies[a.strategy] !== undefined) strategies[a.strategy]++;
    }
    return {
      timestamp: new Date().toISOString(),
      completionStatus: completionStatus,
      totalActions: actions.length,
      succeeded: counts.success,
      failed: counts.failed,
      mismatch: counts.mismatch,
      skipped: counts.skipped,
      uncertain: counts.uncertain,
      totalTimeMs: Date.now() - (syncStartTime.current || Date.now()),
      resolverStats: strategies,
      actions: actions,
    };
  }

  function checkAlertTriggers(report, syncHistory) {
    const triggers = [];
    const allReports = [
      ...(syncHistory || []),
      {
        succeeded: report.succeeded,
        failed: report.failed,
        mismatch: report.mismatch,
        uncertain: report.uncertain,
        resolverStats: report.resolverStats,
        actionStatuses: report.actions.map((a) => ({ key: a.key, status: a.status })),
      },
    ];

    // Trigger 1: Template degradation — success rate below 85% over window
    if (allReports.length >= 2) {
      const totalActions = allReports.reduce(
        (sum, r) => sum + (r.succeeded || 0) + (r.failed || 0) + (r.mismatch || 0) + (r.uncertain || 0),
        0
      );
      const totalSucceeded = allReports.reduce((sum, r) => sum + (r.succeeded || 0), 0);
      const successRate = totalActions > 0 ? Math.round((totalSucceeded / totalActions) * 100) : 100;
      if (successRate < 85) {
        triggers.push({
          type: "template_degradation",
          severity: "HIGH",
          detail: "Success rate " + successRate + "% over last " + allReports.length + " syncs (threshold: 85%)",
        });
      }
    }

    // Trigger 2: Repeated failure — same field fails 3+ consecutive syncs
    if (allReports.length >= 3) {
      const recentStatuses = allReports.slice(-3);
      const allKeys = new Set();
      recentStatuses.forEach((r) => (r.actionStatuses || []).forEach((a) => allKeys.add(a.key)));
      allKeys.forEach((key) => {
        const streak = recentStatuses.every((r) => {
          const entry = (r.actionStatuses || []).find((a) => a.key === key);
          return entry && entry.status === "failed";
        });
        if (streak) {
          triggers.push({
            type: "repeated_failure",
            severity: "HIGH",
            detail: "Field '" + key + "' failed in last 3 consecutive syncs",
          });
        }
      });
    }

    // Trigger 3: Resolver drift — fingerprint strategy on 30%+ actions
    const fpCount = report.resolverStats?.fingerprint || 0;
    const totalResolved = (report.resolverStats?.exact || 0) + (report.resolverStats?.xpath || 0) + fpCount;
    if (totalResolved > 0) {
      const fpPercent = Math.round((fpCount / totalResolved) * 100);
      if (fpPercent >= 30) {
        triggers.push({
          type: "resolver_drift",
          severity: "MEDIUM",
          detail: fpPercent + "% of actions resolved via fingerprint (threshold: 30%)",
        });
      }
    }

    // Trigger 4: Verification mismatches — 3+ in single sync
    if (report.mismatch >= 3) {
      triggers.push({
        type: "verification_mismatches",
        severity: "MEDIUM",
        detail: report.mismatch + " mismatches in this sync (threshold: 3)",
      });
    }

    return triggers;
  }

  function finalizeSyncReport(completionStatus) {
    const syncReport = buildSyncReport(completionStatus);

    if (failedSections.current.length > 0) {
      syncReport.recoveryOffered = true;
      syncReport.recoveryCompleted = false;
      syncReport.sectionsRecovered = 0;
      syncReport.sectionsCancelled = 0;
    }

    // Persist to syncHistory (rolling last 5)
    const syncHistoryEntry = {
      timestamp: syncReport.timestamp,
      completionStatus: syncReport.completionStatus,
      succeeded: syncReport.succeeded,
      failed: syncReport.failed,
      mismatch: syncReport.mismatch,
      resolverStats: syncReport.resolverStats,
      actionStatuses: syncReport.actions.map((a) => ({ key: a.key, status: a.status })),
    };

    const prevHistory = template.current.syncHistory || [];
    const newHistory = [...prevHistory, syncHistoryEntry].slice(-5);
    template.current.syncHistory = newHistory;

    api
      .saveEhrTemplate(template.current.id, { syncHistory: newHistory })
      .catch(() => console.error("EHR Sync: failed to save syncHistory"));

    const triggers = checkAlertTriggers(syncReport, prevHistory);
    if (triggers.length > 0) {
      api
        .reportEhrSyncAlert(
          template.current.id,
          template.current.name || "Unknown Template",
          triggers,
          syncReport,
          newHistory.length
        )
        .catch(() => console.error("EHR Sync: failed to send alert"));
    }

    console.log(
      "EHR Sync: report - " +
        syncReport.succeeded +
        " ok, " +
        syncReport.failed +
        " failed, " +
        syncReport.mismatch +
        " mismatch, " +
        syncReport.skipped +
        " skipped, " +
        syncReport.uncertain +
        " uncertain (" +
        syncReport.totalTimeMs +
        "ms)"
    );
    return syncReport;
  }

  function classifyMismatch(sent, got) {
    // Compare in memory, return classification, discard values immediately
    if (!got || got.trim() === "") return "empty";
    if (!sent || sent.trim() === "") return null; // nothing was sent, can't mismatch
    const sentNorm = sent.toLowerCase().trim().replace(/\s+/g, " ");
    const gotNorm = got.toLowerCase().trim().replace(/\s+/g, " ");
    if (sentNorm === gotNorm) return null; // match
    if (sentNorm.length === 0) return null; // guard against division by zero
    // Check if content is present but different length
    const lenRatio = gotNorm.length / sentNorm.length;
    if (lenRatio >= 0.8 && lenRatio <= 1.2) return "format";
    if (lenRatio > 0 && lenRatio < 0.8) return "partial";
    return "different";
  }

  // Poll the EHR tab until an element exists (event-action postcondition). Uses the
  // shipped existsElement primitive, so no extension release is required. Returns
  // false on timeout, cancellation, or extension failure — never throws.
  async function pollForElement(selector, timeoutMs, elementType) {
    const deadline = Date.now() + timeoutMs;
    const pollMs = 400;
    while (!cancel.current) {
      try {
        // visibleOnly is honored by extension builds that ship it and harmlessly
        // ignored by older ones (they fall back to bare existence).
        const res = await lucasIntegrationRequest({
          action: "existsElement",
          field: { elementType: elementType || "path", element: selector, visibleOnly: true },
        });
        // The answer arrives wrapped ({ _r, _fp }) whenever the extension's
        // resolver captured a fingerprint for the element it found — i.e. on any
        // exact/xpath hit — so a bare `=== true` only ever matched a
        // fingerprint-strategy hit. interpretProbe unwraps both shapes.
        if (interpretProbe(res) === true) return true;
      } catch (e) {
        return false;
      }
      if (Date.now() + pollMs > deadline) break;
      await new Promise((resolve) => setTimeout(resolve, pollMs));
    }
    return false;
  }

  async function verifySetValue(action, sentValue, currentIndex) {
    // Read back the field value and classify the result
    // Values are compared in memory and immediately reduced to a status code
    try {
      const readResponse = await lucasIntegrationRequest({
        action: "getFieldValue",
        fields: [{ elementType: action.elementType, element: action.element }],
      });
      if (readResponse === null || readResponse.error != null) {
        return { verified: false, status: "uncertain", mismatchType: null };
      }
      const gotValue = Array.isArray(readResponse) ? readResponse[0] || "" : typeof readResponse === "string" ? readResponse : "";
      const mismatchType = classifyMismatch(sentValue, gotValue);
      // Values discarded here — only mismatchType persists
      if (mismatchType === null) {
        return { verified: true, status: "success", mismatchType: null };
      }
      return { verified: true, status: "mismatch", mismatchType: mismatchType };
    } catch (e) {
      return { verified: false, status: "uncertain", mismatchType: null };
    }
  }

  async function verifyRichTextValue(action, sentValue, currentIndex) {
    // For wysihtml/tinymce: check non-empty + first 50 chars substring match
    try {
      const readResponse = await lucasIntegrationRequest({
        action: "getFieldValue",
        fields: [{ elementType: action.elementType, element: action.element }],
      });
      if (readResponse === null || readResponse.error != null) {
        return { verified: false, status: "uncertain", mismatchType: null };
      }
      const gotValue = Array.isArray(readResponse) ? readResponse[0] || "" : typeof readResponse === "string" ? readResponse : "";
      // Strip HTML tags for text comparison
      const gotText = gotValue.replace(/<[^>]*>/g, "").trim();
      if (gotText === "") {
        return { verified: true, status: "mismatch", mismatchType: "empty" };
      }
      // Check first 50 chars of sent value appear in the result
      const sentSnippet = sentValue
        .replace(/<[^>]*>/g, "")
        .trim()
        .substring(0, 50)
        .toLowerCase();
      if (gotText.toLowerCase().includes(sentSnippet)) {
        return { verified: true, status: "success", mismatchType: null };
      }
      return { verified: true, status: "mismatch", mismatchType: "different" };
    } catch (e) {
      return { verified: false, status: "uncertain", mismatchType: null };
    }
  }

  async function lucasIntegrationSetValue(action, value, currentIndex, currentRetry) {
    const field = { elementType: action.elementType, element: action.element, value: value };
    if (action.property != null) field.property = action.property;
    if (action.attribute != null) field.attribute = action.attribute;
    if (action.fingerprint) field.fingerprint = action.fingerprint;

    try {
      const response = await lucasIntegrationRequest({
        action: "setFieldValue",
        field: field,
        useCDP: action.useCDP || false,
      });

      // Capture fingerprint from resolver for progressive enrichment
      if (response && response._fp) {
        action._capturedFingerprint = response._fp;
      }

      if (response === null || response.error != null) {
        return { success: "error", errorMsg: response?.error };
      } else {
        // Phase 2: Post-injection verification
        const isRichText = action.elementType === "tinymce" || action.elementType === "wysihtml";
        if (isRichText) {
          // Rich text editors: check non-empty + substring match
          const verResult = await verifyRichTextValue(action, value, currentIndex);
          // Preserve original retry behavior: if empty, retry (editor may still be initializing)
          if (verResult.mismatchType === "empty") {
            return { success: "retry" };
          }
          return { success: "ok", verification: verResult };
        } else {
          const verResult = await verifySetValue(action, value, currentIndex);
          return { success: "ok", verification: verResult };
        }
      }
    } catch (e) {
      return { success: "error", errorMsg: e.message };
    }
  }

  async function lucasIntegrationUpdateECWTable(currentIndex, action, prevValueXPath, newValueValueXPath, newValue, prompt) {
    newValue = lucasIntegrationGetContent(newValue, currentIndex, action);
    try {
      const { prevValue, result } = await lucasIntegrationMergeContent(currentIndex, action, prevValueXPath, newValue, prompt);

      console.log("EHR Sync: Merging content for ECW table");

      const lines = convertHTMLToText(result)
        ?.split("\n")
        .filter((l) => l.trim() !== "");

      // Process all updates in parallel for better performance
      const updatePromises = [];

      for (let i = 0; i < lines?.length; ++i) {
        const line = lines[i]?.trim();

        if (i < prevValue.length) {
          // UPDATE EXISTENT ROW
          console.log("EHR Sync: Updating row " + i);
          const element = "(" + prevValueXPath + ")[" + (i + 1) + "]";
          updatePromises.push(
            lucasIntegrationRequest({
              action: "setFieldValue",
              field: { elementType: "path", element: element, value: line },
            })
          );
        } else {
          console.log("EHR Sync: Adding row " + i);
          // ADD NEW ROW
          const element = newValueValueXPath;
          updatePromises.push(
            lucasIntegrationRequest({
              action: "setFieldValue",
              field: {
                elementType: "path",
                element: element,
                value: line,
                events: {
                  keydown: {
                    key: "Enter",
                    code: "Enter",
                    keyCode: 13,
                    bubbles: true,
                    cancelable: true,
                  },
                },
              },
            })
          );
        }
      }

      for (let i = lines?.length; i < prevValue.length; ++i) {
        // CLEAR EXCESS ROWS
        const element = "(" + prevValueXPath + ")[" + (i + 1) + "]";
        updatePromises.push(
          lucasIntegrationRequest({
            action: "setFieldValue",
            field: { elementType: "path", element: element, value: "" },
          })
        );
      }

      await Promise.all(updatePromises);
      return true;
    } catch (e) {
      console.error("Error in lucasIntegrationUpdateECWTable", e);
      return false;
    }
  }

  async function lucasIntegrationMergeContent(currentIndex, action, prevValueXPath, newValue, prompt) {
    try {
      const response = await lucasIntegrationRequest({
        action: "getFieldValue",
        fields: [{ elementType: "path", element: prevValueXPath }],
      });

      console.log("EHR Sync: Merging content, retrieved previous value (length=" + (response?.length || 0) + ")");
      if (response === null || response[0] === null) {
        // NO PREVIOUS VALUE
        return { prevValue: "", result: newValue };
      }

      if (response.error != null) {
        console.log("EHR Sync: error on merge content: " + response.error);
        lucasIntegrationSyncError(currentIndex, action, "error on merge content: " + response.error);
        throw new Error(response.error);
      }

      let data = await api.ehrProcessContent(prompt, response[0], newValue);
      data = data.data;
      if (data.output != null) {
        if (data.output.error != null) {
          console.warn("EHR Sync: error on merge content: " + data.output.error);
          lucasIntegrationSyncError(currentIndex, action, data.output.error);
          throw new Error(data.output.error);
        }

        if (data.output.result != null) {
          console.log("EHR Sync: Got merge result from API (length=" + (data?.output?.result?.length || 0) + ")");
          return { prevValue: response[0], result: data.output.result };
        } else {
          lucasIntegrationSyncError(currentIndex, action, "Error on setting merged value for action " + currentIndex);
          throw new Error("Error on setting merged value for action " + currentIndex);
        }
      } else {
        lucasIntegrationSyncError(currentIndex, action, "Error on setting merged value for action " + currentIndex);
        throw new Error("Error on setting merged value for action " + currentIndex);
      }
    } catch (e) {
      lucasIntegrationSyncError(currentIndex, action, e);
      throw e;
    }
  }

  const capitalize = (str, lower = true) => {
    return (lower ? str.toLowerCase() : str).replace(/(?:^|\s|["'([{])+\S/g, (match) => match.toUpperCase());
  };

  const ehrSystems = [
    { key: "", label: "" },
    { key: "generic-plaintext", label: "Generic Plain Text" },
    { key: "generic-richtext", label: "Generic Rich Text" },
    { key: "advancedmd", label: "AdvancedMD" },
    { key: "athena", label: "Athena" },
    { key: "caretracker", label: "Care Tracker" },
    { key: "cerner", label: "Oracle Cerner" },
    { key: "credible", label: "Credible" },
    { key: "drchrono", label: "Dr. Chrono" },
    { key: "eclinicalworks", label: "E-ClinicalWorks" },
    { key: "emedicalpractice", label: "eMedicalPractice" },
    { key: "epic", label: "Epic" },
    { key: "experity", label: "Experity" },
    { key: "kinnser", label: "Kinnser" },
    { key: "meditech", label: "Meditech" },
    { key: "nextgen", label: "NextGen" },
    { key: "officeally", label: "Office Ally" },
    { key: "pointclickcare", label: "Point Click Care" },
    { key: "practicefusion", label: "Practice Fusion" },
    { key: "tebra", label: "Tebra" },
    { key: "other", label: "Other" },
  ];

  return {
    error,
    errors,
    syncEHR,
    templates,
    setTemplates,
    selectTemplate,
    startEHRSync,
    stopSync,
    loadingTemplates,
    executing,
    showModalSyncEhr,
    setShowModalSyncEhr,
    restart,
    currentStep,
    totalStep,
    currentSection,
    importEncounterFromEHR,
    importingEncounters,
    getScreenshot,
    ehrSystems,
    getFieldValue,
    highlightFields,
    didRun,
    reset,
    showModalSelectTab,
    setShowModalSelectTab,
    requestTabs,
    executionChat,
    executionTab,
    setExecutionChat,
    setExecutionTab,
    focusActiveTab,
    selectedTabPromise,
    version,
    magicCopy,
    getCopyQueueRemaining,
    smartPasteQueue,
    setSmartPasteQueue,
    failedSections,
    handleSmartPasteComplete,
    handleSmartPasteCancel,
    injectSectionField,
  };
}

export function ProvideEHR({ children }) {
  const values = useEhrProvider();
  return <ehrContext.Provider value={values}>{children}</ehrContext.Provider>;
}

export const useEHR = () => {
  return useContext(ehrContext);
};
